'use client'
import { markBillingAsPaidChecked } from '@/lib/billing/mark-billing-paid'

import { billingCategoryLabel } from '@/lib/billing/billing-categories'
import { BillingAttachmentsPanel } from '@/components/billing/billing-attachments'
import BillingComposition from '@/components/billing/billing-composition'
import BillingChildrenSections from '@/components/billing/billing-children-sections'
import BillingInfoSection from '@/components/billing/BillingInfoSection'
import RequestRefundModal, {
    RefundSubmitData,
} from '@/components/billing/request-refund-modal'
import { submitBillingRefund } from '@/lib/api/financial/billing-refund-api'
import LinkAppointmentModal from '@/components/billing/LinkAppointmentModal'
import ConfirmModal from '@/components/confirm-modal'
import ActionsMenu, { ActionMenuItem } from '@/components/actions-menu'
import {
    Billing,
    BillingAttachment,
    BillingOrigin,
    BillingStatus,
} from '@/lib/api/common/interfaces/billing.interface'
import {
    cancelBilling,
    deleteBilling,
    getBilling,
    requestBillingRefund,
    syncBillingWithProvider,
    sendBillingPaymentEmail,
    generateBillingPayment,
    unlinkBillingFromAppointment,
    getSandboxPaymentAvailability,
    payBillingInSandbox,
} from '@/lib/api/financial/financial-billing-api'
import { submitPayoutBatch } from '@/lib/api/financial/payout-batch-api'
import {
    getBillingPayoutPreview,
    type PlannedPayout,
} from '@/lib/api/financial/payout-preview-api'
import {
    getExamPayoutItemSplit,
    splitExamPayoutByItem,
    type PayoutItemSplitPreview,
} from '@/lib/api/exams/exam-api'
import { BILLING_STATUS_LABELS } from '@/lib/billing/payment-labels'
import PayoutBatchItemsList from '@/components/billing/payout-batch-items-list'
import {
    getJournalEntries,
    processBillingAccounting,
    JournalEntry,
} from '@/lib/api/financial/financial-accounting-api'
import { getTerminals } from '@/lib/api/financial/financial-terminal-api'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useOfflineSettlementPolicy } from '@/lib/hooks/use-offline-settlement-policy'
import { useInstantRefundPolicy } from '@/lib/hooks/use-instant-refund-policy'
import { getOriginLabels, ORIGIN_ROUTES } from '@/lib/billing/billing-origin'
import { useServiceLabel } from '@/lib/hooks/use-service-label'
import { isEditableBillingStatus } from '@/lib/billing/billing-editable-status'
import { SocketContext } from '@/lib/providers/socket-provider'
import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import {
    MdAccountBalance,
    MdAdd,
    MdArrowBack,
    MdAttachMoney,
    MdCalendarToday,
    MdCancel,
    MdReplay,
    MdCheckCircle,
    MdClose,
    MdContentCopy,
    MdDelete,
    MdEdit,
    MdEmail,
    MdLink,
    MdLocalHospital,
    MdNotes,
    MdDocumentScanner,
    MdEventNote,
    MdHourglassTop,
    MdOpenInNew,
    MdPayment,
    MdPerson,
    MdQrCode,
    MdReceipt,
    MdSend,
    MdStore,
    MdVerified,
    MdWarning,
    MdLinkOff,
    MdSearch,
} from 'react-icons/md'
import {
    getLedger,
    linkLedgerToBilling,
    unlinkLedgerFromBilling,
    LedgerEntry,
} from '@/lib/api/financial/ledger-api'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useAuth } from '@/store/hooks'
import { formatPhoneNumber } from '@/lib/form-state/util'

/** Tela de detalhes de cada origem possível de uma cobrança. */
const STATUS_LABELS: Record<BillingStatus, string> = {
    PENDING: 'Pendente',
    PAID: 'Pago',
    OVERDUE: 'Vencido',
    CANCELLED: 'Cancelado',
    REFUNDED: 'Estornado',
    PARTIALLY_REFUNDED: 'Parcialmente Estornado',
    PARTIALLY_PAID: 'Parcialmente Pago',
    IN_DISPUTE: 'Em Disputa',
    WRITTEN_OFF: 'Baixado',
}

const STATUS_BADGE_CLASS: Record<BillingStatus, string> = {
    PENDING: 'badge-warning',
    PAID: 'badge-success text-white',
    OVERDUE: 'badge-error text-white',
    CANCELLED: 'badge-ghost',
    REFUNDED: 'badge-info text-white',
    PARTIALLY_REFUNDED: 'badge-info',
    PARTIALLY_PAID: 'badge-warning',
    IN_DISPUTE: 'badge-error text-white',
    WRITTEN_OFF: 'badge-ghost',
}

const PAYMENT_METHOD_LABELS: Record<string, string> = {
    PIX: 'PIX',
    BOLETO: 'Boleto',
    CREDIT_CARD: 'Cartão de Crédito',
    DEBIT_CARD: 'Cartão de Débito',
    CASH: 'Dinheiro',
    BANK_TRANSFER: 'Transferência Bancária',
    CHECK: 'Cheque',
    TERMINAL: 'Maquininha (terminal)',
    OTHER: 'Outro',
}

function formatCurrency(cents: number): string {
    return (cents / 100).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    })
}

function formatDate(dateStr: string): string {
    // Datas no formato YYYY-MM-DD são interpretadas como UTC meia-noite,
    // o que causa dia errado em UTC-3. Forçar como meio-dia para evitar o problema.
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        return new Date(`${dateStr}T12:00:00`).toLocaleDateString('pt-BR')
    }
    return new Date(dateStr).toLocaleDateString('pt-BR', {
        timeZone: 'America/Sao_Paulo',
    })
}

function formatDateTime(dateStr: string): string {
    return new Date(dateStr).toLocaleString('pt-BR', {
        timeZone: 'America/Sao_Paulo',
    })
}

/**
 * Dados do pagamento lidos do comprovante pela IA (gravados no anexo pelo cron
 * `receipt-review-tasks`): banco, pagador, data, valor e código da transação —
 * mais o veredito de autenticidade.
 *
 * É a informação que o financeiro procura ao conferir uma baixa: de onde veio o
 * dinheiro e se o documento é confiável.
 */
/**
 * Aprovação e liquidação da conta: em que pé está a aprovação, como o dinheiro
 * saiu (transferência pela conta digital ou pagamento externo) e a conciliação.
 *
 * Era informação que existia no banco e não aparecia em lugar nenhum — quem
 * conferia a conta tinha de adivinhar se a transferência saiu, falhou ou se o
 * financeiro pagou por fora.
 */
/** Fatia de repasse de UM atendimento dentro de uma cobrança agrupada. */
interface PayoutShareDetail {
    id: number
    sourceType: string
    sourceId: number
    amount: number
    partnerAmount?: number | null
    professionalAmount?: number | null
    hospitalAmount?: number | null
    suppliesAmount?: number | null
    partner?: { id: number; name: string; tradingName?: string | null } | null
    professional?: { id: number; name: string } | null
    hospital?: { id: number; name: string; tradingName?: string | null } | null
}

/** Rótulos de quem recebe. Os mesmos quatro tipos do detalhe do procedimento. */
const RECIPIENT_KIND_LABELS: Record<string, string> = {
    PARTNER: 'Parceiro',
    PROFESSIONAL: 'Profissional',
    HOSPITAL: 'Hospital',
    SUPPLIER: 'Fornecedor',
}

/**
 * Repasses de uma cobrança que cobre VÁRIOS atendimentos.
 *
 * O card normal lê `billing.partnerAmount` e irmãos, que aqui estão zerados de
 * propósito: uma cobrança com três laboratórios não tem "o parceiro". Sem esta
 * versão, a tela simplesmente não mostrava repasse nenhum na cobrança agrupada —
 * justamente onde saber quem recebe o quê é mais importante.
 *
 * ⚠️ O mesmo destinatário pode aparecer em DOIS atendimentos (consulta + exame no
 * mesmo laboratório). Ao pagar, sai UMA conta a pagar somada — por isso o total
 * por destinatário aparece agregado no rodapé.
 */
/**
 * Exame cujo repasse ao parceiro pode ser separado por item: linha clínica de UM exame,
 * com a conta a pagar já lançada, em aberto e ainda não separada.
 */
function itemSplitExamOf(p: PlannedPayout): number | null {
    if (p.origin !== 'CLINICAL' || p.kind !== 'PARTNER') return null
    if (p.sources.length !== 1 || p.sources[0].type !== 'EXAM') return null
    if (!p.payableId || p.itemPayables?.length) return null
    if (p.payableStatus !== 'PENDING' && p.payableStatus !== 'OVERDUE')
        return null
    return p.sources[0].id
}

function SharedPayoutCard({
    billingId,
    shares,
    planned,
    total,
    origins,
    coveredBy,
    canSplitByItem,
    onPayoutsChanged,
}: {
    billingId: number
    /** Operador pode separar a conta do repasse por item (permissão de editar cobrança). */
    canSplitByItem: boolean
    /** Recarrega os repasses depois de uma separação. */
    onPayoutsChanged: () => void
    shares: PayoutShareDetail[]
    /**
     * O que a cobrança vai gerar de repasse, vindo do backend pelo MESMO caminho da
     * geração. Substituiu a soma dos campos da fatia, que errava por baixo: a taxa do
     * anestesista não tem campo lá, e o total de material vinha sem destinatário.
     */
    planned: PlannedPayout[]
    total: number
    /** Atendimentos da cobrança — traz o status, que diz quem foi cancelado. */
    origins: BillingOrigin[]
    /**
     * Nome do convênio, quando é ele quem paga. `total` já vem com o valor faturado
     * (e não com o `amount` zerado), então a margem sai correta — isto serve para a
     * tela dizer de onde vem a receita, já que o dinheiro ainda não entrou.
     */
    coveredBy?: string | null
}) {
    /**
     * Atendimento cancelado continua com fatia (é o registro de que ele esteve na
     * cobrança e do estorno pedido), mas não deve ser somado nem apresentado como
     * repasse a fazer — o repasse dele já foi cancelado.
     */
    const originLabels = getOriginLabels(useServiceLabel())

    const cancelada = (sourceType: string, sourceId: number) =>
        origins.find(o => o.type === sourceType && o.id === sourceId)
            ?.status === 'CANCELLED'

    const vivos = planned.filter(
        p => !p.sources.every(s => cancelada(s.type, s.id))
    )
    const totalRepasses = vivos.reduce((sum, p) => sum + p.amount, 0)
    const margem = total - totalRepasses

    /**
     * Uma conta a pagar por linha: destinatário + origem. Anestesista e material saem
     * SEPARADOS do repasse clínico do mesmo destinatário de propósito — o financeiro
     * precisa poder pagar um e não o outro. Ver docs/billing-system.md §7.
     */
    const ORIGIN_ORDER: Record<PlannedPayout['origin'], number> = {
        CLINICAL: 0,
        ANESTHETIST: 1,
        SUPPLY: 2,
    }
    const ordenados = [...vivos].sort(
        (a, b) =>
            ORIGIN_ORDER[a.origin] - ORIGIN_ORDER[b.origin] ||
            (a.name ?? '').localeCompare(b.name ?? '')
    )

    const multiplos = shares.length > 1

    /**
     * Separação do repasse por item. A regra é uma conta a pagar por exame — a maioria
     * dos parceiros cobra assim —, então o botão só aparece no exame com dois ou mais
     * itens com valor de repasse. A prévia vem do backend, que calcula o valor de cada
     * item pela mesma regra que gerou a conta.
     */
    const [splitPreviews, setSplitPreviews] = useState<
        Record<string, PayoutItemSplitPreview>
    >({})
    const [splitTarget, setSplitTarget] = useState<{
        examId: number
        preview: PayoutItemSplitPreview
    } | null>(null)
    const [splitting, setSplitting] = useState(false)

    useEffect(() => {
        if (!canSplitByItem) return
        let cancelled = false
        const eligible = planned
            .map(p => ({ key: p.payoutKey, examId: itemSplitExamOf(p) }))
            .filter(
                (e): e is { key: string; examId: number } => e.examId != null
            )
        Promise.all(
            eligible.map(async e => {
                const res = await getExamPayoutItemSplit(
                    e.examId,
                    billingId
                ).catch(() => null)
                return [e.key, res?.success ? res.result : null] as const
            })
        ).then(entries => {
            if (cancelled) return
            const next: Record<string, PayoutItemSplitPreview> = {}
            for (const [key, preview] of entries) {
                if (preview) next[key] = preview
            }
            setSplitPreviews(next)
        })
        return () => {
            cancelled = true
        }
    }, [planned, billingId, canSplitByItem])

    const openSplit = (p: PlannedPayout) => {
        const examId = itemSplitExamOf(p)
        const preview = splitPreviews[p.payoutKey]
        if (!examId || !preview) return
        if (!preview.canSplit) {
            toast.error(
                preview.reason || 'Este repasse não pode ser separado por item.'
            )
            return
        }
        setSplitTarget({ examId, preview })
    }

    const confirmSplit = async () => {
        if (!splitTarget) return
        setSplitting(true)
        try {
            const res = await splitExamPayoutByItem(
                splitTarget.examId,
                billingId
            )
            if (!res.success) {
                toast.error(res.error || 'Não foi possível separar o repasse.')
                return
            }
            toast.success(
                `Repasse separado em ${res.result?.payableIds.length ?? 0} contas a pagar.`
            )
            setSplitTarget(null)
            onPayoutsChanged()
        } finally {
            setSplitting(false)
        }
    }

    return (
        <div className="bg-base-100 rounded-xl border border-base-300/50 p-6">
            <h2 className="mb-1 flex items-center gap-2 text-lg font-semibold text-base-content">
                <MdAttachMoney className="text-xl" />
                Repasses programados
            </h2>
            <p className="mb-4 text-xs text-base-content/50">
                {multiplos
                    ? `Esta cobrança cobre ${shares.length} atendimentos. Cada linha vira uma conta a pagar quando o cliente paga.`
                    : 'Cada linha vira uma conta a pagar quando o cliente paga.'}
            </p>

            {ordenados.length === 0 ? (
                <p className="text-xs text-base-content/50">
                    Sem repasse — o valor fica integralmente com a empresa.
                </p>
            ) : (
                <div className="flex flex-col gap-2">
                    {ordenados.map(p => (
                        <div
                            key={p.payoutKey}
                            className="rounded-lg border border-base-300/60 p-3"
                        >
                            <div className="flex items-center justify-between gap-3">
                                <div className="min-w-0">
                                    <p className="truncate text-sm font-medium text-base-content">
                                        {p.name ?? `${p.kind} #${p.entityId}`}
                                    </p>
                                    <p className="text-xs text-base-content/50">
                                        {RECIPIENT_KIND_LABELS[p.kind] ??
                                            p.kind}
                                        {' · '}
                                        {p.label}
                                        {multiplos && p.sources.length > 0
                                            ? ` · ${p.sources
                                                  .map(
                                                      s =>
                                                          `${originLabels[s.type] || s.type} #${s.id}`
                                                  )
                                                  .join(', ')}`
                                            : ''}
                                    </p>
                                </div>
                                <div className="shrink-0 text-right">
                                    <span className="tabular-nums text-warning">
                                        {formatCurrency(p.amount)}
                                    </span>
                                    {/* Conta a pagar já gerada: o link é o que permite
                                    conferir se o que a tela prometeu foi o que nasceu. */}
                                    {p.payableId && !p.itemPayables?.length && (
                                        <Link
                                            to={`/financial/billings/${p.payableId}`}
                                            className="block text-xs text-primary hover:underline"
                                        >
                                            Conta #{p.payableId}
                                        </Link>
                                    )}
                                    {splitPreviews[p.payoutKey]?.canSplit && (
                                        <button
                                            type="button"
                                            className="btn btn-ghost btn-xs mt-1 text-primary"
                                            onClick={() => openSplit(p)}
                                        >
                                            Separar por item
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Exame com vários itens cuja separação está travada: o motivo
                            fica à vista, em vez de um botão que só falha no clique. */}
                            {splitPreviews[p.payoutKey] &&
                                !splitPreviews[p.payoutKey].canSplit &&
                                splitPreviews[p.payoutKey].pieces.filter(
                                    piece => piece.amount > 0
                                ).length >= 2 && (
                                    <p className="mt-2 text-xs leading-snug text-base-content/50">
                                        Separação por item indisponível:{' '}
                                        {splitPreviews[p.payoutKey].reason}
                                    </p>
                                )}

                            {/* Repasse separado por item: uma conta por item, cada uma paga
                            quando o parceiro cobrar aquele item. */}
                            {!!p.itemPayables?.length && (
                                <div className="mt-3 flex flex-col gap-2 border-t border-base-300/50 pt-3">
                                    <p className="text-xs font-medium text-base-content/60">
                                        Separado por item
                                    </p>
                                    {p.itemPayables.map(item => (
                                        <div
                                            key={item.payableId}
                                            className="flex items-center justify-between gap-3 text-xs"
                                        >
                                            <span className="min-w-0 truncate text-base-content/80">
                                                {item.label ??
                                                    `Item #${item.itemId ?? '?'}`}
                                            </span>
                                            <span className="flex shrink-0 items-center gap-3">
                                                <span className="text-base-content/50">
                                                    {BILLING_STATUS_LABELS[
                                                        item.status
                                                    ] ?? item.status}
                                                </span>
                                                <span className="tabular-nums">
                                                    {formatCurrency(
                                                        item.amount
                                                    )}
                                                </span>
                                                <Link
                                                    to={`/financial/billings/${item.payableId}`}
                                                    className="text-primary hover:underline"
                                                >
                                                    Conta #{item.payableId}
                                                </Link>
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            <div className="mt-4 flex flex-col gap-1 border-t border-base-300/50 pt-3 text-sm">
                <div className="flex items-center justify-between gap-3">
                    <span className="text-base-content/60">Total cobrado</span>
                    <span className="tabular-nums">
                        {formatCurrency(total)}
                    </span>
                </div>
                <div className="flex items-center justify-between gap-3">
                    <span className="text-base-content/60">
                        Total de repasses
                    </span>
                    <span className="tabular-nums text-warning">
                        {formatCurrency(totalRepasses)}
                    </span>
                </div>
                <div className="flex items-center justify-between gap-3 font-semibold">
                    <span>Margem</span>
                    <span
                        className={`tabular-nums ${margem < 0 ? 'text-error' : 'text-success'}`}
                    >
                        {formatCurrency(margem)}
                    </span>
                </div>
                {coveredBy && (
                    <p className="pt-1 text-xs leading-tight text-base-content/60">
                        Receita coberta por {coveredBy}. O repasse é gerado na
                        hora, e o valor é faturado ao convênio — o dinheiro
                        entra quando a fatura do período for paga.
                    </p>
                )}
            </div>

            <ConfirmModal
                isOpen={!!splitTarget}
                onClose={() => setSplitTarget(null)}
                onConfirm={confirmSplit}
                title="Separar repasse por item"
                message={
                    splitTarget
                        ? `A conta a pagar #${splitTarget.preview.payableId} vira uma conta por item, para o parceiro que cobra cada item à parte. Vencimento e destinatário continuam os mesmos.`
                        : ''
                }
                loading={splitting}
                confirmButtonText="Separar"
                confirmButtonClass="btn-primary"
            >
                {splitTarget && (
                    <div className="mt-4 flex flex-col gap-2 rounded-lg border border-base-300/60 p-3 text-sm">
                        {splitTarget.preview.pieces
                            .filter(piece => piece.amount > 0)
                            .map(piece => (
                                <div
                                    key={piece.itemId}
                                    className="flex items-center justify-between gap-3"
                                >
                                    <span className="min-w-0 truncate">
                                        {piece.label}
                                    </span>
                                    <span className="shrink-0 tabular-nums">
                                        {formatCurrency(piece.amount)}
                                    </span>
                                </div>
                            ))}
                        <div className="flex items-center justify-between gap-3 border-t border-base-300/50 pt-2 font-semibold">
                            <span>Total da conta</span>
                            <span className="tabular-nums">
                                {formatCurrency(
                                    splitTarget.preview.payableAmount ?? 0
                                )}
                            </span>
                        </div>
                    </div>
                )}
            </ConfirmModal>
        </div>
    )
}

function PaymentTrailSection({ billing }: { billing: Billing }) {
    const transfer = billing.transfer
    const externalVia = billing.externalTransferVia
    const hasApproval =
        !!billing.approvalStatus && billing.approvalStatus !== 'NOT_REQUIRED'

    if (!hasApproval && !transfer && !externalVia && !billing.isReconciled)
        return null

    const APPROVAL_LABELS: Record<string, string> = {
        PENDING_APPROVAL: 'Aguardando aprovação',
        APPROVED: 'Aprovada',
        REJECTED: 'Rejeitada',
    }
    const APPROVAL_BADGE: Record<string, string> = {
        PENDING_APPROVAL: 'badge-warning',
        APPROVED: 'badge-success text-white',
        REJECTED: 'badge-error text-white',
    }

    const TRANSFER_LABELS: Record<string, string> = {
        PENDING_APPROVAL: 'Aguardando aprovação',
        APPROVED: 'Autorizada',
        REJECTED: 'Rejeitada',
        PROCESSING: 'Em processamento no banco',
        DONE: 'Concluída',
        FAILED: 'Falhou',
        CANCELLED: 'Cancelada',
    }
    const TRANSFER_BADGE: Record<string, string> = {
        PROCESSING: 'badge-info text-white',
        DONE: 'badge-success text-white',
        FAILED: 'badge-error text-white',
        CANCELLED: 'badge-ghost',
        APPROVED: 'badge-warning',
        REJECTED: 'badge-error text-white',
        PENDING_APPROVAL: 'badge-warning',
    }

    const destination = transfer?.pixKey
        ? `PIX ${transfer.pixKeyType ? `(${transfer.pixKeyType})` : ''} ${transfer.pixKey}`
        : transfer?.bankAccountNumber
          ? `Banco ${transfer.bankCode} · Ag. ${transfer.bankAgency} · Conta ${transfer.bankAccountNumber}`
          : null

    return (
        <div className="bg-base-100 rounded-xl border border-base-300/50 p-6 lg:col-span-2">
            <h2 className="text-lg font-semibold text-base-content mb-4 flex items-center gap-2">
                <MdAccountBalance className="text-xl" />
                Aprovação e pagamento
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {hasApproval && (
                    <div>
                        <p className="text-xs text-base-content/50">
                            Aprovação
                        </p>
                        <span
                            className={`badge ${APPROVAL_BADGE[billing.approvalStatus!] || 'badge-ghost'} mt-1`}
                        >
                            {APPROVAL_LABELS[billing.approvalStatus!] ||
                                billing.approvalStatus}
                        </span>
                        {billing.approvedBy && (
                            <p className="text-xs text-base-content/60 mt-1">
                                por {billing.approvedBy.name}
                                {billing.approvedAt &&
                                    ` · ${new Date(billing.approvedAt).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}`}
                            </p>
                        )}
                    </div>
                )}

                {transfer && (
                    <>
                        <div>
                            <p className="text-xs text-base-content/50">
                                Transferência (conta digital)
                            </p>
                            <span
                                className={`badge ${TRANSFER_BADGE[transfer.status] || 'badge-ghost'} mt-1`}
                            >
                                {TRANSFER_LABELS[transfer.status] ||
                                    transfer.status}
                            </span>
                            {transfer.executedAt && (
                                <p className="text-xs text-base-content/60 mt-1">
                                    {new Date(
                                        transfer.executedAt
                                    ).toLocaleString('pt-BR', {
                                        dateStyle: 'short',
                                        timeStyle: 'short',
                                    })}
                                </p>
                            )}
                        </div>

                        {destination && (
                            <div>
                                <p className="text-xs text-base-content/50">
                                    Destino
                                </p>
                                <p className="font-medium break-words">
                                    {destination}
                                </p>
                            </div>
                        )}

                        {transfer.providerTransferId && (
                            <div>
                                <p className="text-xs text-base-content/50">
                                    ID no provedor
                                </p>
                                <p className="font-mono text-sm break-all">
                                    {transfer.providerTransferId}
                                </p>
                            </div>
                        )}
                    </>
                )}

                {externalVia && (
                    <div>
                        <p className="text-xs text-base-content/50">
                            Pagamento
                        </p>
                        <span className="badge badge-info text-white gap-1 mt-1">
                            <MdAccountBalance className="text-sm" />
                            Transferida via {externalVia}
                        </span>
                        <p className="text-xs text-base-content/60 mt-1">
                            Fora da conta digital
                            {billing.externalTransferBy &&
                                ` · marcada por ${billing.externalTransferBy.name}`}
                            {billing.externalTransferAt &&
                                ` · ${new Date(billing.externalTransferAt).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}`}
                        </p>
                    </div>
                )}

                <div>
                    <p className="text-xs text-base-content/50">Conciliação</p>
                    <span
                        className={`badge mt-1 ${billing.isReconciled ? 'badge-success text-white' : 'badge-ghost'}`}
                    >
                        {billing.isReconciled ? 'Conciliada' : 'Não conciliada'}
                    </span>
                    {billing.reconciledBy && (
                        <p className="text-xs text-base-content/60 mt-1">
                            por {billing.reconciledBy.name}
                        </p>
                    )}
                </div>
            </div>

            {transfer?.failReason && (
                <div className="mt-4 p-4 rounded-xl bg-error/5 border border-error/20">
                    <p className="text-sm font-semibold text-base-content mb-1">
                        Motivo da falha
                    </p>
                    <p className="text-sm text-base-content/70">
                        {transfer.failReason}
                    </p>
                </div>
            )}

            {billing.reconciliationNotes && (
                <p className="text-sm text-base-content/70 mt-4">
                    {billing.reconciliationNotes}
                </p>
            )}
        </div>
    )
}

function ReceiptDataSection({
    attachments,
}: {
    attachments?: BillingAttachment[] | null
}) {
    // Vale o comprovante válido mais recente que já foi lido.
    const analyzed = (attachments || [])
        .filter(att => !att.rejected && att.reading?.reviewedAt)
        .pop()

    if (!analyzed?.reading) return null

    const { reading } = analyzed
    const receipt = reading.data
    const findings = reading.findings || []

    const rows: { label: string; value?: string | null }[] = [
        { label: 'Instituição', value: receipt?.banco },
        { label: 'Pagador', value: receipt?.pagador },
        { label: 'Beneficiário', value: receipt?.beneficiario },
        { label: 'Tipo', value: receipt?.tipo },
        {
            label: 'Valor no comprovante',
            value:
                receipt?.valor != null
                    ? (receipt.valor / 100).toLocaleString('pt-BR', {
                          style: 'currency',
                          currency: 'BRL',
                      })
                    : null,
        },
        {
            label: 'Data do pagamento',
            value: receipt?.data_pagamento
                ? new Date(receipt.data_pagamento).toLocaleString('pt-BR', {
                      dateStyle: 'short',
                      timeStyle: 'short',
                  })
                : null,
        },
        { label: 'Código da transação', value: receipt?.codigo_transacao },
    ].filter(row => !!row.value)

    return (
        <div className="bg-base-100 rounded-xl border border-base-300/50 p-6 lg:col-span-2">
            <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
                <h2 className="text-lg font-semibold text-base-content flex items-center gap-2">
                    <MdDocumentScanner className="text-xl" />
                    Dados do comprovante
                </h2>
                {reading.readable === false ? (
                    <span className="badge badge-warning gap-1">
                        <MdWarning className="text-sm" />
                        Ilegível
                    </span>
                ) : reading.trustworthy ? (
                    <span className="badge badge-success text-white gap-1">
                        <MdVerified className="text-sm" />
                        Validado pela IA
                    </span>
                ) : (
                    <span className="badge badge-error text-white gap-1">
                        <MdWarning className="text-sm" />
                        Suspeito
                    </span>
                )}
            </div>

            {rows.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {rows.map(row => (
                        <div key={row.label}>
                            <p className="text-xs text-base-content/50">
                                {row.label}
                            </p>
                            <p className="font-medium text-base-content break-words">
                                {row.value}
                            </p>
                        </div>
                    ))}
                </div>
            ) : (
                <p className="text-sm text-base-content/50">
                    A leitura não extraiu dados do documento.
                </p>
            )}

            {findings.length > 0 && (
                <div className="mt-4 p-4 rounded-xl bg-error/5 border border-error/20">
                    <p className="text-sm font-semibold text-base-content mb-2">
                        O que a leitura apontou
                    </p>
                    <ul className="list-disc list-inside text-sm text-base-content/70 space-y-1">
                        {findings.map((finding, i) => (
                            <li key={i}>{finding}</li>
                        ))}
                    </ul>
                </div>
            )}

            <p className="text-xs text-base-content/40 mt-4">
                Lido em{' '}
                {new Date(reading.reviewedAt).toLocaleString('pt-BR', {
                    dateStyle: 'short',
                    timeStyle: 'short',
                })}
                {receipt?.confianca != null &&
                    ` · confiança ${Math.round(receipt.confianca * 100)}%`}
            </p>
        </div>
    )
}

function BillingAccountingSection({ billingId }: { billingId: number }) {
    const navigate = useNavigate()
    const [entries, setEntries] = useState<JournalEntry[]>([])
    const [loading, setLoading] = useState(true)
    const [processing, setProcessing] = useState(false)

    const { isCompany } = useAuth()

    /**
     * Os endpoints de contabilidade exigem `financial:journal_entries` — ser
     * COMPANY não basta. Sem a permissão, a seção some e a API nem é chamada
     * (antes ela era chamada sempre e voltava 403 no console).
     */
    const { canRead: canReadEntries, canCreate: canCreateEntries } =
        useResourcePermissions('financial', 'journal_entries')

    const canSeeAccounting = isCompany && canReadEntries

    useEffect(() => {
        if (!canSeeAccounting) {
            setLoading(false)
            return
        }
        loadEntries()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [billingId, canSeeAccounting])

    const loadEntries = async () => {
        setLoading(true)
        try {
            const res = await getJournalEntries({ billingId, limit: 10 })
            if (res.success && res.result) setEntries(res.result.items)
        } catch {
            /* ignore */
        } finally {
            setLoading(false)
        }
    }

    const handleProcess = async () => {
        setProcessing(true)
        try {
            const res = await processBillingAccounting(billingId)
            if (res.success && res.result) {
                const entry = res.result
                if (entry.status === 'PENDING' || entry.lines.length === 0) {
                    toast.info(
                        'Nenhuma regra encontrada. Classifique manualmente.'
                    )
                    navigate(`/financial/journal-entries/${entry.id}`)
                } else {
                    toast.success(
                        'Lançamento contábil classificado automaticamente'
                    )
                    loadEntries()
                }
            } else {
                toast.error(res.error || 'Erro ao processar')
            }
        } catch {
            toast.error('Erro ao processar contabilidade')
        } finally {
            setProcessing(false)
        }
    }

    const fmtCurrency = (cents: number) =>
        (cents / 100).toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL',
        })

    const STATUS_BADGE: Record<string, string> = {
        PENDING: 'badge-warning',
        POSTED: 'badge-info',
        REVIEWED: 'badge-success',
    }
    const STATUS_LABEL: Record<string, string> = {
        PENDING: 'Pendente',
        POSTED: 'Lançado',
        REVIEWED: 'Revisado',
    }

    if (!canSeeAccounting) {
        return null
    }

    return (
        <div className="bg-base-100 rounded-xl border border-base-300/50 p-6 lg:col-span-2">
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-base-content flex items-center gap-2">
                    <MdReceipt className="text-xl" />
                    Contabilidade
                </h2>
                {entries.length === 0 && !loading && canCreateEntries && (
                    <button
                        className="btn btn-secondary btn-sm gap-1"
                        onClick={handleProcess}
                        disabled={processing}
                    >
                        {processing ? (
                            <span className="loading loading-spinner loading-xs"></span>
                        ) : (
                            <MdAdd size={16} />
                        )}
                        Classificar agora
                    </button>
                )}
            </div>

            {loading && (
                <div className="flex justify-center py-4">
                    <div className="loading loading-spinner loading-sm text-secondary"></div>
                </div>
            )}

            {!loading && entries.length === 0 && (
                <p className="text-sm text-base-content/50">
                    Nenhum lançamento contábil vinculado.
                </p>
            )}

            {!loading &&
                entries.map(entry => (
                    <div
                        key={entry.id}
                        className="border border-base-300/50 rounded-lg p-3 mb-2"
                    >
                        <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                                <span
                                    className={`badge badge-xs ${STATUS_BADGE[entry.status] || 'badge-ghost'}`}
                                >
                                    {STATUS_LABEL[entry.status] || entry.status}
                                </span>
                                <span className="badge badge-xs badge-ghost">
                                    {entry.origin === 'AI_CLASSIFIED'
                                        ? 'IA'
                                        : entry.origin === 'AUTOMATIC'
                                          ? 'Automático'
                                          : 'Manual'}
                                </span>
                                {entry.aiConfidence !== null && (
                                    <span className="text-[10px] text-base-content/40">
                                        {Math.round(entry.aiConfidence * 100)}%
                                        confiança
                                    </span>
                                )}
                            </div>
                            <button
                                className="btn btn-ghost btn-xs"
                                onClick={() =>
                                    navigate(
                                        `/financial/journal-entries/${entry.id}`
                                    )
                                }
                            >
                                Ver detalhes
                            </button>
                        </div>
                        <p className="text-sm mb-2">{entry.description}</p>
                        {entry.lines.length > 0 ? (
                            entry.lines.map(line => (
                                <div
                                    key={line.id}
                                    className="flex items-center justify-between text-xs py-0.5"
                                >
                                    <span
                                        className={
                                            line.type === 'DEBIT'
                                                ? 'text-error'
                                                : 'text-success'
                                        }
                                    >
                                        {line.type === 'DEBIT' ? 'D' : 'C'}:{' '}
                                        {line.account.code} -{' '}
                                        {line.account.title}
                                    </span>
                                    <span className="font-mono">
                                        {fmtCurrency(line.amount)}
                                    </span>
                                </div>
                            ))
                        ) : (
                            <div className="bg-warning/10 rounded-lg p-3 mt-1">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm text-base-content/70">
                                        Valor da cobrança
                                    </span>
                                    <span className="font-mono font-semibold">
                                        {entry.billing
                                            ? fmtCurrency(entry.billing.amount)
                                            : '-'}
                                    </span>
                                </div>
                                <p className="text-xs text-warning mb-2">
                                    Nenhuma regra encontrada. Defina as contas
                                    de débito e crédito do plano de contas.
                                </p>
                                <button
                                    className="btn btn-warning btn-sm btn-block gap-1"
                                    onClick={() =>
                                        navigate(
                                            `/financial/journal-entries/${entry.id}`
                                        )
                                    }
                                >
                                    Classificar no plano de contas
                                </button>
                            </div>
                        )}
                        {entry.aiReasoning && (
                            <p className="text-[11px] text-base-content/40 mt-2 italic">
                                {entry.aiReasoning}
                            </p>
                        )}
                    </div>
                ))}
        </div>
    )
}

interface BillingDetailPageProps {
    /** Renderiza embutido (modal) para este id, ignorando a rota. */
    idOverride?: number
    /** Substitui o "voltar para a lista" por fechar o modal. */
    onBack?: () => void
}

export default function BillingDetailPage({
    idOverride,
    onBack,
}: BillingDetailPageProps = {}) {
    const navigate = useNavigate()
    const { id: paramId } = useParams<{ id: string }>()
    const id = idOverride ? String(idOverride) : paramId
    /** No modal, sair da lista significa fechar; na rota, navegar. */
    const goBack = () => (onBack ? onBack() : navigate('/financial/billings'))
    const { canUpdate, canDelete } = useResourcePermissions(
        'financial',
        'billings'
    )
    const { socket, isConnected } = useContext(SocketContext)
    const serviceLabels = useServiceLabel()
    const originLabels = getOriginLabels(serviceLabels)
    // Baixa direta de recebimentos por fora do sistema (ver isDirectSettlement).
    const { autoSettle: offlineAutoSettle } = useOfflineSettlementPolicy()
    // Só muda o tooltip do botão: o modal é que conduz os dois fluxos.
    const { allowInstantRefund } = useInstantRefundPolicy()
    const [billing, setBilling] = useState<Billing | null>(null)
    const [loading, setLoading] = useState(true)
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [deleting, setDeleting] = useState(false)
    /** Vínculo manual com atendimento (cliente pagou por fora, via chave PIX). */
    const [showUnlinkModal, setShowUnlinkModal] = useState(false)
    const [unlinking, setUnlinking] = useState(false)
    const [showLinkAppointmentModal, setShowLinkAppointmentModal] =
        useState(false)
    const [showPayModal, setShowPayModal] = useState(false)
    const [paying, setPaying] = useState(false)
    const [checkingProvider, setCheckingProvider] = useState(false)
    const [showRefundModal, setShowRefundModal] = useState(false)
    const [requestingRefund, setRequestingRefund] = useState(false)
    const [showCancelModal, setShowCancelModal] = useState(false)
    const [cancelling, setCancelling] = useState(false)
    const [showGeneratePaymentModal, setShowGeneratePaymentModal] =
        useState(false)
    const [generatingPayment, setGeneratingPayment] = useState(false)
    const [showChargeModal, setShowChargeModal] = useState(false)
    const [charging, setCharging] = useState(false)
    const [chargeBillingType, setChargeBillingType] = useState<
        'PIX' | 'BOLETO' | 'CREDIT_CARD' | 'DEBIT_CARD'
    >('PIX')
    const [chargeTerminalId, setChargeTerminalId] = useState<number | null>(
        null
    )
    const [chargeTerminals, setChargeTerminals] = useState<any[]>([])
    const [sendingEmail, setSendingEmail] = useState(false)
    const [copiedField, setCopiedField] = useState<string | null>(null)
    // Pagamento de teste do PIX: só existe com o gateway em sandbox, e quem decide
    // isso é o backend (fora dele o POST responde 403).
    const [sandboxPayAvailable, setSandboxPayAvailable] = useState(false)
    const [payingInSandbox, setPayingInSandbox] = useState(false)
    const { isCompany } = useAuth()

    // Conciliação manual: vínculo de movimentação do extrato (LedgerEntry).
    const [showLinkModal, setShowLinkModal] = useState(false)
    const [ledgerSearch, setLedgerSearch] = useState('')
    const [ledgerResults, setLedgerResults] = useState<LedgerEntry[]>([])
    const [ledgerLoading, setLedgerLoading] = useState(false)
    const [selectedLedger, setSelectedLedger] = useState<LedgerEntry | null>(
        null
    )
    const [linking, setLinking] = useState(false)
    const ledgerSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

    /**
     * Repasses que uma solicitação agrupada soma, e o envio dela para a fila.
     *
     * A tela mostrava só o total: um número de período inteiro, sem dizer de quais
     * atendimentos ele veio.
     */
    // Totais do lote inteiro: quem os carrega é a própria lista, que os recebe
    // agregados do banco — a tela conhece apenas a página aberta.
    const [batchSummary, setBatchSummary] = useState({
        total: 0,
        totalAmount: 0,
    })
    const handleBatchSummary = useCallback(
        (next: { total: number; totalAmount: number }) => setBatchSummary(next),
        []
    )
    const [submittingBatch, setSubmittingBatch] = useState(false)

    /**
     * Repasses previstos, de endpoint próprio.
     *
     * Não vêm dentro da cobrança porque a projeção mora no módulo de repasse, que já
     * importa o de cobrança — a volta fecharia um ciclo. Ver
     * `PayoutOrchestratorService.previewBillingPayouts`.
     */
    const [plannedPayouts, setPlannedPayouts] = useState<PlannedPayout[]>([])

    useEffect(() => {
        if (id) {
            loadBilling()
            loadPlannedPayouts()
        }
    }, [id])

    /**
     * Manda a solicitação configurada para a fila de aprovação.
     *
     * O botão vive aqui, e não só na edição, porque salvar a edição traz o operador
     * para esta tela — sem ele, seria preciso entrar em "Editar" de novo só para
     * clicar em enviar.
     */
    const handleSubmitBatch = async () => {
        if (!id) return
        setSubmittingBatch(true)
        try {
            const res = await submitPayoutBatch(parseInt(id, 10))
            if (res.success) {
                toast.success(
                    'Solicitação enviada para aprovação com o demonstrativo anexado.'
                )
                navigate('/financial/bills-approvals')
            } else {
                toast.error(res.error || 'Erro ao enviar para aprovação')
            }
        } catch {
            toast.error('Erro ao enviar para aprovação')
        } finally {
            setSubmittingBatch(false)
        }
    }

    const handleUnlinkAppointment = async () => {
        if (!id) return
        setUnlinking(true)
        try {
            const res = await unlinkBillingFromAppointment(parseInt(id, 10))
            if (res.success && res.result) {
                toast.success(res.result.message)
                setShowUnlinkModal(false)
                loadBilling()
            } else {
                toast.error(res.error || 'Não foi possível desfazer o vínculo.')
            }
        } catch {
            toast.error('Não foi possível desfazer o vínculo.')
        } finally {
            setUnlinking(false)
        }
    }

    // Atualização em tempo real via WebSocket
    useEffect(() => {
        if (!socket || !isConnected || !id) return

        const eventName = `billing-payment-${id}`

        const handleBillingUpdate = () => {
            loadBilling()
            // O pagamento é o que faz as contas a pagar nascerem: sem recarregar aqui,
            // o card continuaria mostrando a projeção sem o link para cada uma.
            loadPlannedPayouts()
        }

        socket.on(eventName, handleBillingUpdate)

        return () => {
            socket.off(eventName, handleBillingUpdate)
        }
    }, [socket, isConnected, id])

    const loadPlannedPayouts = async () => {
        if (!id) return
        try {
            const res = await getBillingPayoutPreview(parseInt(id, 10))
            setPlannedPayouts(res.success && res.result ? res.result : [])
        } catch {
            // Silencioso de propósito: o card some, o resto da tela continua. Quem não
            // tem permissão de financeiro simplesmente não vê os repasses.
            setPlannedPayouts([])
        }
    }

    const loadBilling = async () => {
        if (!id) return

        setLoading(true)
        try {
            const response = await getBilling(parseInt(id, 10))
            if (response.success && response.result) {
                setBilling(response.result)
            } else {
                toast.error('Cobrança não encontrada')
                goBack()
            }
        } catch (error) {
            toast.error('Erro ao carregar cobrança')
            goBack()
        } finally {
            setLoading(false)
        }
    }

    /**
     * Cobrança gerada no gateway: antes da baixa manual, pergunta ao provedor qual é a
     * verdade. O webhook pode se perder — o cliente pagou, o dinheiro entrou na conta
     * digital, e a cobrança segue pendente aqui. Se o gateway confirma, a baixa acontece
     * por ele (com repasse, nota fiscal e confirmação) e o modal nem abre; se não
     * confirma, o operador é avisado e segue o fluxo manual, que é legítimo (PIX direto
     * na conta, dinheiro) e exige comprovante.
     */
    const handlePayClick = async () => {
        if (!billing) return

        // Sem gateway (dinheiro, maquininha, transferência avulsa): baixa manual de
        // sempre — comprovante obrigatório e, se não for operador, fila de aprovação.
        if (!billing.providerPaymentId) {
            setShowPayModal(true)
            return
        }

        // Com gateway, quem decide é o provedor, e SÓ ele. O dinheiro entra por lá, então
        // não faz sentido exigir comprovante nem aprovação de operador para um fato que o
        // Asaas confirma sozinho. Nunca cai no fluxo manual.
        setCheckingProvider(true)
        try {
            const res = await syncBillingWithProvider(billing.id)

            if (!res.success || !res.result) {
                toast.error(
                    res.error ||
                        'Não foi possível consultar o gateway. Tente novamente.'
                )
                return
            }

            const { outcome, message } = res.result

            if (outcome === 'PAID' || outcome === 'ALREADY_PAID') {
                toast.success(message)
            } else if (outcome === 'REFUNDED' || outcome === 'CANCELLED') {
                toast.warning(message)
            } else {
                // AWAITING_PAYMENT / OVERDUE — o gateway ainda não viu o dinheiro.
                toast.info(message)
            }

            await loadBilling()
        } catch {
            toast.error(
                'Não foi possível consultar o gateway. Tente novamente.'
            )
        } finally {
            setCheckingProvider(false)
        }
    }

    /**
     * Estorno a partir da cobrança.
     *
     * Dois desfechos possíveis, e quem decide é a política da empresa: sem "Estorno
     * sem aprovação", nada é devolvido aqui — abre solicitação e a fila decide, com
     * senha do operador; com ela ligada, o dinheiro volta neste gesto (o modal já
     * pediu modo e senha). O helper roteia pelo payload que o modal montou.
     */
    const handleRequestRefund = async (data: RefundSubmitData) => {
        if (!billing || requestingRefund) return

        setRequestingRefund(true)
        try {
            const response = await submitBillingRefund(billing.id, data)
            if (response.success) {
                toast.success(response.message)
                setShowRefundModal(false)
                await loadBilling()
            } else {
                // O backend recusa quando o repasse ao parceiro já foi pago — a mensagem
                // dele diz qual, e é o que o operador precisa ler.
                toast.error(response.error || 'Erro ao estornar')
            }
        } catch {
            toast.error('Erro ao estornar')
        } finally {
            setRequestingRefund(false)
        }
    }

    /**
     * Baixa disparada pelo card da cobrança (`BillingInfoSection`), que traz o próprio
     * modal de confirmação e a própria checagem de comprovante — por isso aqui só o
     * efeito, sem o `showPayModal` do botão do cabeçalho.
     */
    const handleMarkAsPaidFromCard = async () => {
        if (!billing) return
        const response = await markBillingAsPaidChecked(billing.id)
        if (response.success) {
            const result = response.result as {
                pending?: boolean
                message?: string
            }
            if (result?.pending) {
                toast.info(
                    result.message ||
                        'A solicitação de aprovação para marcar como pago foi enviada'
                )
            } else {
                toast.success('Cobrança marcada como paga')
            }
            await loadBilling()
        } else {
            toast.error(response.error || 'Erro ao marcar como paga')
        }
    }

    const handleMarkAsPaid = async () => {
        if (!billing) return
        // Guard de duplo clique: dois envios criavam duas solicitações de baixa.
        if (paying) return

        // Dinheiro não exige comprovante (não há comprovante bancário em espécie). O
        // controle segue pela fila de aprovação. Demais métodos manuais continuam exigindo.
        if (billing.paymentMethod !== 'CASH' && !billing.attachments?.length) {
            toast.error(
                'Anexe o comprovante de pagamento na cobrança antes de marcá-la como paga.'
            )
            setShowPayModal(false)
            return
        }

        setPaying(true)
        try {
            const response = await markBillingAsPaidChecked(billing.id)
            if (response.success) {
                const result = response.result as {
                    pending?: boolean
                    message?: string
                }
                if (result?.pending) {
                    toast.info(
                        result.message ||
                            'A solicitação de aprovação para marcar como pago foi enviada'
                    )
                } else {
                    toast.success('Cobrança marcada como paga')
                }
                setShowPayModal(false)
                await loadBilling()
            } else {
                toast.error(response.error || 'Erro ao marcar como paga')
            }
        } catch (error) {
            toast.error('Erro ao marcar como paga')
        } finally {
            setPaying(false)
        }
    }

    const handleCancel = async () => {
        if (!billing) return

        setCancelling(true)
        try {
            const response = await cancelBilling(billing.id)
            if (response.success) {
                toast.success('Cobrança cancelada com sucesso')
                setShowCancelModal(false)
                await loadBilling()
            } else {
                toast.error(response.error || 'Erro ao cancelar cobrança')
            }
        } catch (error) {
            toast.error('Erro ao cancelar cobrança')
        } finally {
            setCancelling(false)
        }
    }

    const handleDelete = async () => {
        if (!billing) return

        setDeleting(true)
        try {
            const response = await deleteBilling(billing.id)
            if (response.success) {
                toast.success('Cobrança excluída com sucesso')
                goBack()
            } else {
                toast.error(response.error || 'Erro ao excluir cobrança')
            }
        } catch (error) {
            toast.error('Erro ao excluir cobrança')
        } finally {
            setDeleting(false)
        }
    }

    const openChargeModal = async () => {
        setShowChargeModal(true)
        try {
            const res = await getTerminals()
            if (res.success && res.result) {
                setChargeTerminals(res.result.filter((t: any) => t.active))
            }
        } catch {}
    }

    const handleCharge = async () => {
        // Geracao de cobranca via provider sera reintroduzida na fase BaaS.
        toast.error(
            'Geração automática de cobrança temporariamente desativada (migração para BaaS em andamento).'
        )
        setShowChargeModal(false)
    }

    /**
     * Métodos que o painel gera no provider. Só PIX e boleto: cartão precisa dos
     * dados do cartão do pagador (coletados no checkout), não dá para gerar aqui.
     */
    const canGeneratePayment =
        !!billing?.paymentMethod &&
        ['PIX', 'BOLETO'].includes(billing.paymentMethod)

    /** Cobrança já tem dados de pagamento gerados no provider. */
    const hasPaymentData = !!(
        billing?.providerPaymentId ||
        billing?.pixCopyPaste ||
        billing?.pixQrCodeBase64 ||
        billing?.bankSlipUrl
    )

    /**
     * Gera/regera os dados de pagamento. O backend resolve pelo método da
     * cobrança (PIX/boleto) e, se já havia dados, cancela a cobrança antiga no
     * provider antes de emitir a nova — por isso o clique com dados existentes
     * passa antes pelo modal de confirmação.
     */
    const runGeneratePayment = async () => {
        if (!billing) return
        setGeneratingPayment(true)
        try {
            const response = await generateBillingPayment(billing.id)
            if (response.success) {
                toast.success('Dados de pagamento gerados')
                await loadBilling()
            } else {
                toast.error(
                    response.error || 'Erro ao gerar dados de pagamento'
                )
            }
        } catch {
            toast.error('Erro ao gerar dados de pagamento')
        } finally {
            setGeneratingPayment(false)
            setShowGeneratePaymentModal(false)
        }
    }

    const handleGeneratePayment = () => {
        // Com dados já gerados, confirma a substituição (cancela o atual + gera
        // novo). Sem dados, gera direto.
        if (hasPaymentData) {
            setShowGeneratePaymentModal(true)
        } else {
            runGeneratePayment()
        }
    }

    const canPayInSandbox =
        !!billing &&
        !!billing.pixCopyPaste &&
        (billing.status === 'PENDING' || billing.status === 'OVERDUE')

    useEffect(() => {
        if (!billing?.id || !canPayInSandbox) return
        let active = true
        getSandboxPaymentAvailability(billing.id)
            .then(res => {
                if (active) setSandboxPayAvailable(!!res.result?.available)
            })
            .catch(() => {
                if (active) setSandboxPayAvailable(false)
            })
        return () => {
            active = false
        }
    }, [billing?.id, canPayInSandbox])

    const handlePayInSandbox = async () => {
        if (!billing) return
        setPayingInSandbox(true)
        try {
            const res = await payBillingInSandbox(billing.id)
            if (res.success) {
                // A baixa chega pelo webhook e a tela recarrega sozinha pelo socket.
                toast.success(
                    'PIX pago no sandbox. A baixa chega pelo webhook em instantes.'
                )
            } else {
                toast.error(res.error || 'Não foi possível pagar no sandbox')
            }
        } catch {
            toast.error('Não foi possível pagar no sandbox')
        } finally {
            setPayingInSandbox(false)
        }
    }

    const handleCopy = async (text: string, field: string) => {
        try {
            await navigator.clipboard.writeText(text)
            setCopiedField(field)
            toast.success('Copiado!')
            setTimeout(() => setCopiedField(null), 2000)
        } catch {
            toast.error('Erro ao copiar')
        }
    }

    const handleSendEmail = async () => {
        if (!billing) return

        setSendingEmail(true)
        try {
            const response = await sendBillingPaymentEmail(billing.id)
            if (response.success) {
                toast.success('Email enviado com sucesso')
            } else {
                toast.error(response.error || 'Erro ao enviar email')
            }
        } catch {
            toast.error('Erro ao enviar email')
        } finally {
            setSendingEmail(false)
        }
    }

    const getCheckoutUrl = () => {
        if (!billing?.publicToken) return null
        const baseUrl = window.location.origin
        return `${baseUrl}/pay/${billing.publicToken}`
    }

    const isPayable = billing?.type === 'PAYABLE'
    // Cobertura por convênio: quem deve é a operadora. O paciente continua no
    // registro (é dele o atendimento), mas não é o pagador.
    const isHealthPlanPayer = billing?.payerType === 'HEALTH_PLAN'

    /** Agendamento (ou pedido) que originou a cobrança — resolvido no backend. */
    const origin = billing?.origin || null
    /**
     * Todos os atendimentos cobertos pela cobrança. Com o rateio de repasse uma
     * cobrança pode cobrir vários — e aí a tela precisa listar, senão o financeiro
     * vê o total sem saber do que é.
     */
    const origins = billing?.origins?.length
        ? billing.origins
        : origin
          ? [origin]
          : []

    /**
     * Atendimento a que a cobrança está atrelada hoje — o que a troca e o desvínculo
     * precisam nomear. Vem da FK (`origins`), com `metadata.sourceType` como reserva
     * para o vínculo manual gravado antes de o atendimento apontar de volta.
     */
    const currentLink =
        origins.length === 1
            ? {
                  type: origins[0].type,
                  typeLabel: originLabels[origins[0].type] ?? 'Atendimento',
                  id: origins[0].id,
                  title: origins[0].title,
              }
            : (billing as any)?.metadata?.sourceType &&
                (billing as any)?.metadata?.sourceId
              ? {
                    type: (billing as any).metadata.sourceType as string,
                    typeLabel:
                        originLabels[(billing as any).metadata.sourceType] ??
                        'Atendimento',
                    id: Number((billing as any).metadata.sourceId),
                    title: null,
                }
              : null

    /**
     * Tem complemento/parcela atrelada? Só então vale chamar esta de "principal" —
     * numa cobrança única o rótulo sugeriria uma segunda que não existe.
     */
    const hasChildBillings = ((billing as any)?.adjustments || []).some(
        (a: { status?: string }) => a.status !== 'CANCELLED'
    )

    /**
     * As filhas se dividem em duas naturezas que a relação `adjustments` não separa:
     *
     *  - **forma de pagamento** (`metadata.paymentSplit`): parte do MESMO valor, paga
     *    de outro jeito (parte PIX, parte dinheiro);
     *  - **complemento/parcela**: valor A MAIS, cobrado depois.
     *
     * Chamar as duas de "cobrança complementar" faz a conta a pagar dividida parecer
     * dever o dobro — a parte em dinheiro apareceria como se somasse ao total.
     */
    const allChildren: any[] = (billing as any)?.adjustments || []
    const paymentSplitChildren = allChildren.filter(
        a => a?.metadata?.paymentSplit === true
    )
    const complementChildren = allChildren.filter(
        a => a?.metadata?.paymentSplit !== true
    )

    /**
     * Já existe solicitação (baixa/cancelamento) aguardando um operador financeiro.
     *
     * Sem isto, a tela mostrava o botão "Pago" ativo mesmo com o pedido em fila — o
     * usuário pedia pela tela do exame, abria a cobrança, não via nada indicando a
     * pendência e clicava de novo. Resultado: duas solicitações do mesmo lançamento,
     * as duas aprovadas, e a baixa executada duas vezes.
     */
    const isAwaitingApproval = billing?.approvalStatus === 'PENDING_APPROVAL'

    /**
     * Qual solicitação está segurando a cobrança e em que aba da fila ela mora.
     * O link leva à aba certa, filtrada pelo número da cobrança e com o item
     * marcado — antes era "aguardando aprovação" e o operador procurava sozinho.
     */
    const pendingApproval = (() => {
        if (!billing || !isAwaitingApproval) return null
        const request = billing.pendingApprovalRequest
        const params = new URLSearchParams({ search: String(billing.id) })

        if (!request) {
            if (billing.type !== 'PAYABLE') return null
            params.set('tab', 'payables')
            params.set('focus', String(billing.id))
            return {
                title: 'Conta a pagar aguardando aprovação',
                description:
                    'Um operador financeiro precisa aprovar esta conta para o pagamento sair.',
                linkLabel: 'Abrir em Contas a Pagar',
                href: `/financial/bills-approvals?${params}`,
                requestedBy: null as string | null,
                requestedAt: null as string | null,
            }
        }

        const byAction = {
            REFUND: {
                tab: 'refunds',
                title: 'Estorno aguardando aprovação',
                description:
                    'O pedido de devolução deste valor está na fila de estornos.',
                linkLabel: 'Abrir em Estornos',
            },
            MARK_PAID: {
                tab: 'actions',
                title: 'Baixa manual aguardando aprovação',
                description:
                    'O pedido para marcar esta cobrança como paga está na fila de aprovações.',
                linkLabel: 'Abrir em Aprovações',
            },
            CANCEL: {
                tab: 'actions',
                title: 'Cancelamento aguardando aprovação',
                description:
                    'O pedido de cancelamento desta cobrança está na fila de aprovações.',
                linkLabel: 'Abrir em Aprovações',
            },
            SETTLE_PROCEDURE: {
                tab: 'actions',
                title: 'Quitação aguardando aprovação',
                description:
                    'O pedido para quitar o procedimento pelo valor pago está na fila de aprovações.',
                linkLabel: 'Abrir em Aprovações',
            },
        }[request.action]
        if (!byAction) return null

        params.set('tab', byAction.tab)
        params.set('focus', String(request.id))
        return {
            ...byAction,
            href: `/financial/bills-approvals?${params}`,
            requestedBy: request.requestedBy?.name ?? null,
            requestedAt: request.createdAt,
        }
    })()

    /**
     * Solicitação AGRUPADA de repasse: uma conta a pagar que soma os repasses de um
     * destinatário num período. Ver `docs/billing-payout-batch.md`.
     */
    const isPayoutBatch = (billing as any)?.isPayoutBatch === true
    /**
     * Ainda em configuração: nasceu fora da fila para o financeiro definir vencimento
     * e formas de pagamento. Enquanto está assim, o aprovador não a vê — e é por isso
     * que "Enviar para aprovação" precisa estar VISÍVEL aqui, e não escondido no menu:
     * salvar a edição traz o operador para esta tela, e sem o botão ele teria de voltar
     * para a edição só para clicar.
     */
    const awaitingBatchSetup =
        isPayoutBatch && (billing as any)?.metadata?.awaitingSetup === true

    /**
     * Solicitação recusada pelo aprovador.
     *
     * NÃO há reenvio: recusar libera os repasses de volta ao pool e cancela a
     * solicitação (ver `BillingApprovalService.reject`). Reenviar mandaria à fila uma
     * conta a pagar sem composição nenhuma. O caminho é corrigir o que motivou a
     * recusa e gerar um lote novo — os repasses estão lá, esperando.
     */
    const rejectedBatch =
        isPayoutBatch && billing?.approvalStatus === 'REJECTED'

    /**
     * Cobrança que o gateway liquida sozinho (PIX/boleto/cartão). Não oferece
     * baixa manual: o Asaas dá baixa por webhook, e pedir comprovante/aprovação
     * para isso só polui a fila — foi o que um operador acabou fazendo. Com
     * charge emitida ainda dá para "Verificar pagamento" (consulta o gateway);
     * sem charge, não há o que baixar nem o que verificar aqui.
     */
    // Pagador convênio nunca passa pelo gateway: o backend não gera charge nem
    // envia link (BillingService.create / sendPaymentLinkToClient). Refletir isso
    // aqui evita oferecer ao operador botões que o backend recusaria em silêncio.
    const isGatewayBilling =
        !isHealthPlanPayer &&
        !!billing?.paymentMethod &&
        ['PIX', 'BOLETO', 'CREDIT_CARD'].includes(billing.paymentMethod)

    // Métodos que o cliente paga ONLINE pelo checkout (`/pay/{token}`) — para
    // esses vale exibir o link. Dinheiro, maquininha, transferência manual e
    // "outro" são pagos presencialmente/fora do gateway, então não têm link.
    // Antes o link só aparecia para CREDIT_CARD; PIX (que é o caso mais comum
    // de link) ficava de fora, embora o publicToken exista para toda cobrança e
    // o checkout aceite PIX normalmente.
    const CHECKOUT_LINK_METHODS = ['PIX', 'CREDIT_CARD', 'DEBIT_CARD', 'BOLETO']
    const hasCheckoutLink =
        !isHealthPlanPayer &&
        !!billing?.paymentMethod &&
        CHECKOUT_LINK_METHODS.includes(billing.paymentMethod)

    // Baixa manual só é permitida com o comprovante de pagamento anexado — exceto
    // dinheiro, que não tem comprovante bancário (o controle fica na fila de aprovação).
    const isCashPayment = billing?.paymentMethod === 'CASH'

    /**
     * Recebido por fora do sistema (dinheiro, maquininha, outro) com a baixa
     * direta habilitada nas Configurações Financeiras: o backend dispensa
     * comprovante e fila, então nem o botão bloqueia nem o aviso de comprovante
     * pendente faz sentido.
     */
    const isDirectSettlement =
        offlineAutoSettle &&
        !!billing?.paymentMethod &&
        ['CASH', 'TERMINAL', 'OTHER'].includes(billing.paymentMethod)

    const hasPaymentProof =
        isCashPayment ||
        isDirectSettlement ||
        (billing?.attachments || []).some(att => !att.rejected)

    // Detecta vínculo manual de extrato pelo marcador deixado nas notas
    // (`[ledger:<id>]`) — habilita o botão "Desvincular".
    const linkedLedgerId = (() => {
        const m = billing?.reconciliationNotes?.match(/\[ledger:(\d+)\]/)
        return m ? Number(m[1]) : null
    })()

    const searchLedger = (term: string) => {
        setLedgerSearch(term)
        if (ledgerSearchTimer.current) clearTimeout(ledgerSearchTimer.current)
        ledgerSearchTimer.current = setTimeout(async () => {
            setLedgerLoading(true)
            try {
                const res = await getLedger({
                    unlinkedOnly: true,
                    creditsOnly: true,
                    search: term.trim() || undefined,
                    limit: 30,
                })
                if (res.success && res.result) setLedgerResults(res.result)
            } catch {
                /* ignore */
            } finally {
                setLedgerLoading(false)
            }
        }, 350)
    }

    const openLinkModal = () => {
        setSelectedLedger(null)
        setLedgerSearch('')
        setLedgerResults([])
        setShowLinkModal(true)
        searchLedger('')
    }

    const handleLinkLedger = async () => {
        if (!billing || !selectedLedger) return
        setLinking(true)
        try {
            const res = await linkLedgerToBilling(selectedLedger.id, billing.id)
            if (res.success) {
                toast.success(
                    res.result?.settled
                        ? 'Movimentação vinculada e cobrança baixada'
                        : 'Movimentação vinculada'
                )
                setShowLinkModal(false)
                await loadBilling()
            } else {
                toast.error(res.error || 'Erro ao vincular movimentação')
            }
        } catch {
            toast.error('Erro ao vincular movimentação')
        } finally {
            setLinking(false)
        }
    }

    const handleUnlinkLedger = async () => {
        if (!billing || !linkedLedgerId) return
        setLinking(true)
        try {
            const res = await unlinkLedgerFromBilling(linkedLedgerId)
            if (res.success) {
                toast.success(
                    res.result?.reverted
                        ? 'Movimentação desvinculada e baixa revertida'
                        : 'Movimentação desvinculada'
                )
                await loadBilling()
            } else {
                toast.error(res.error || 'Erro ao desvincular')
            }
        } catch {
            toast.error('Erro ao desvincular')
        } finally {
            setLinking(false)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando cobrança...
                </p>
            </div>
        )
    }

    if (!billing) {
        return null
    }

    return (
        <div className="flex flex-col gap-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => goBack()}
                        className="btn btn-ghost btn-circle btn-sm"
                    >
                        <MdArrowBack className="text-xl" />
                    </button>
                    <div className="flex items-center gap-3">
                        <div
                            className={`w-12 h-12 rounded-xl bg-gradient-to-br ${
                                isPayable
                                    ? 'from-red-500 to-red-600'
                                    : 'from-emerald-500 to-emerald-600'
                            } flex items-center justify-center shadow-lg`}
                        >
                            <MdReceipt className="text-2xl text-white" />
                        </div>
                        <div className="flex flex-col">
                            <div className="flex items-center gap-2">
                                <h1 className="text-xl font-bold text-base-content">
                                    {isPayable ? 'Conta a Pagar' : 'Cobrança'} #
                                    {billing.id}
                                </h1>
                            </div>{' '}
                            <div
                                className={`badge badge-sm ${isPayable ? 'badge-error' : 'badge-success'}`}
                            >
                                {isPayable
                                    ? 'Conta a Pagar'
                                    : 'Conta a Receber'}
                            </div>
                            {billing.description && (
                                <p className="text-sm text-base-content/60">
                                    {billing.description}
                                </p>
                            )}
                        </div>
                    </div>
                </div>

                {(canUpdate || canDelete) && (
                    <div className="flex items-center gap-2">
                        {/* Ação primária da solicitação agrupada em configuração.
                            VISÍVEL, e não no menu: salvar a edição traz o operador
                            para cá, e o passo seguinte dele é exatamente este. Sem o
                            botão, ele teria de voltar para "Editar" só para clicar. */}
                        {canUpdate && awaitingBatchSetup && (
                            <button
                                onClick={handleSubmitBatch}
                                disabled={submittingBatch}
                                className="btn btn-secondary gap-2"
                                title="Gera o demonstrativo com as formas de pagamento definidas e envia para a fila de aprovação"
                            >
                                {submittingBatch ? (
                                    <span className="loading loading-spinner loading-sm" />
                                ) : (
                                    <MdSend className="text-xl" />
                                )}
                                <span className="hidden sm:inline">
                                    Enviar para aprovação
                                </span>
                            </button>
                        )}

                        {/* Ação primária: dar baixa numa cobrança pendente/vencida. Fica
                            VISÍVEL (fora do menu) por ser a ação central e por ter estado
                            próprio (loading do gateway, desabilitada sem comprovante). */}
                        {canUpdate &&
                            (billing.status === 'PENDING' ||
                                billing.status === 'OVERDUE') &&
                            !isAwaitingApproval &&
                            // Gateway sem charge emitida: nada a baixar nem a
                            // verificar. O caminho é gerar os dados de pagamento
                            // (menu) ou trocar o método, não baixa manual.
                            (!isGatewayBilling ||
                                !!billing.providerPaymentId) && (
                                <button
                                    onClick={handlePayClick}
                                    disabled={
                                        checkingProvider ||
                                        (!hasPaymentProof &&
                                            !billing.providerPaymentId)
                                    }
                                    title={
                                        billing.providerPaymentId
                                            ? 'Consultar o gateway e, se o pagamento tiver entrado, dar baixa'
                                            : isDirectSettlement
                                              ? 'Marcar cobrança como paga (recebida por fora do sistema)'
                                              : hasPaymentProof
                                                ? 'Marcar cobrança como paga'
                                                : 'Anexe o comprovante de pagamento antes de marcar como paga'
                                    }
                                    className="btn btn-success btn-sm gap-2"
                                >
                                    {checkingProvider ? (
                                        <span className="loading loading-spinner loading-sm" />
                                    ) : (
                                        <MdCheckCircle className="text-xl" />
                                    )}
                                    <span className="hidden sm:inline">
                                        {checkingProvider
                                            ? 'Consultando...'
                                            : billing.providerPaymentId
                                              ? 'Verificar pagamento'
                                              : 'Pago'}
                                    </span>
                                </button>
                            )}

                        {/* Ação primária: estornar uma cobrança já paga (a receber).
                            Convênio fica de fora: ela consta paga por convenção (nasce
                            quitada com valor zero para liberar o repasse), não porque
                            alguém pagou — não há dinheiro do paciente a devolver, e o
                            backend recusa o estorno. */}
                        {canUpdate &&
                            !isPayable &&
                            !isHealthPlanPayer &&
                            !isAwaitingApproval &&
                            [
                                'PAID',
                                'PARTIALLY_PAID',
                                'PARTIALLY_REFUNDED',
                            ].includes(billing.status) && (
                                <button
                                    onClick={() => setShowRefundModal(true)}
                                    disabled={requestingRefund}
                                    className="btn btn-info btn-outline gap-2"
                                    title={
                                        allowInstantRefund
                                            ? 'Estornar agora, sem passar pela fila de aprovação'
                                            : 'Solicitar estorno ao operador financeiro'
                                    }
                                >
                                    {requestingRefund ? (
                                        <span className="loading loading-spinner loading-sm" />
                                    ) : (
                                        <MdReplay className="text-xl" />
                                    )}
                                    <span className="hidden sm:inline">
                                        Estornar
                                    </span>
                                </button>
                            )}

                        {/* Demais ações recolhidas no dropdown (destrutivas abaixo do
                            divisor), igual às telas de exame/consulta/procedimento. */}
                        <ActionsMenu
                            items={
                                [
                                    // Repetida no menu, além do botão visível: é onde
                                    // o operador procura as ações do lançamento.
                                    ...(canUpdate && awaitingBatchSetup
                                        ? [
                                              {
                                                  key: 'submit-batch',
                                                  label: 'Enviar para aprovação',
                                                  icon: <MdSend />,
                                                  onClick: handleSubmitBatch,
                                              },
                                          ]
                                        : []),
                                    ...(canUpdate &&
                                    (billing.status === 'PENDING' ||
                                        billing.status === 'OVERDUE') &&
                                    !isAwaitingApproval &&
                                    !isPayable &&
                                    canGeneratePayment
                                        ? [
                                              {
                                                  key: 'generate-payment',
                                                  // Genérico: o backend resolve o
                                                  // método (PIX/boleto). Regera se
                                                  // já houver dados (confirma antes).
                                                  label: hasPaymentData
                                                      ? 'Regerar dados de pagamento'
                                                      : 'Gerar dados de pagamento',
                                                  icon: <MdQrCode />,
                                                  onClick:
                                                      handleGeneratePayment,
                                              },
                                          ]
                                        : []),
                                    ...(canUpdate &&
                                    isEditableBillingStatus(billing.status)
                                        ? [
                                              {
                                                  key: 'edit',
                                                  label: 'Editar',
                                                  icon: <MdEdit />,
                                                  onClick: () =>
                                                      navigate(
                                                          `/financial/billings/${billing.id}/edit`
                                                      ),
                                              },
                                          ]
                                        : []),
                                    // Cobrança SOLTA vincula; cobrança já vinculada
                                    // TROCA de atendimento pela mesma ação (é o que o
                                    // operador quer em quase todo desvínculo). Fora:
                                    // complemento de outra cobrança, que segue o
                                    // atendimento da principal.
                                    //
                                    // A detecção é `origins` (a FK do atendimento), não
                                    // `metadata.sourceType`: as cobranças importadas do
                                    // legado não têm esse metadata, e por ele a tela
                                    // oferecia "Vincular" em cobrança já vinculada.
                                    ...(canUpdate &&
                                    !isPayable &&
                                    billing.status !== 'CANCELLED' &&
                                    !(billing as any).parentBillingId &&
                                    origins.length <= 1
                                        ? [
                                              {
                                                  key: 'link-appointment',
                                                  label: currentLink
                                                      ? 'Trocar atendimento'
                                                      : 'Vincular',
                                                  icon: <MdEventNote />,
                                                  onClick: () =>
                                                      setShowLinkAppointmentModal(
                                                          true
                                                      ),
                                              },
                                          ]
                                        : []),
                                    ...(canUpdate &&
                                    !isPayable &&
                                    currentLink &&
                                    !(billing as any).parentBillingId &&
                                    origins.length <= 1
                                        ? [
                                              {
                                                  key: 'unlink-appointment',
                                                  label: 'Desvincular do atendimento',
                                                  icon: <MdLinkOff />,
                                                  onClick: () =>
                                                      setShowUnlinkModal(true),
                                                  danger: true,
                                              },
                                          ]
                                        : []),
                                    ...(canUpdate &&
                                    (billing.status === 'PENDING' ||
                                        billing.status === 'OVERDUE') &&
                                    !isAwaitingApproval
                                        ? [
                                              {
                                                  key: 'cancel',
                                                  label: 'Cancelar cobrança',
                                                  icon: <MdCancel />,
                                                  onClick: () =>
                                                      setShowCancelModal(true),
                                                  danger: true,
                                              },
                                          ]
                                        : []),
                                    // Cobrança de atendimento não se exclui pelo
                                    // financeiro. Mesma detecção do vínculo (`origins`,
                                    // com o metadata como reserva): pelo metadata sozinho,
                                    // as importadas do legado — que não o têm — podiam ser
                                    // excluídas deixando o atendimento sem cobrança.
                                    ...(canDelete && !currentLink
                                        ? [
                                              {
                                                  key: 'delete',
                                                  label: 'Excluir',
                                                  icon: <MdDelete />,
                                                  onClick: () =>
                                                      setShowDeleteModal(true),
                                                  danger: true,
                                              },
                                          ]
                                        : []),
                                ] as ActionMenuItem[]
                            }
                        />
                    </div>
                )}
            </div>

            {/* Vários atendimentos na mesma cobrança: lista, com o valor de cada um
                vindo do próprio atendimento (a cobrança tem só o total). */}
            {origins.length > 1 && (
                <div className="bg-base-100 rounded-xl border border-base-300/50 p-4">
                    <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center shrink-0">
                            <MdEventNote className="text-xl text-secondary" />
                        </div>
                        <div>
                            <p className="font-medium text-base-content">
                                {origins.length} atendimentos nesta cobrança
                            </p>
                            <p className="text-xs text-base-content/60">
                                Cada um com o seu prestador e o seu repasse.
                            </p>
                        </div>
                    </div>
                    <ul className="divide-y divide-base-300/50">
                        {origins.map(item => (
                            <li
                                key={`${item.type}-${item.id}`}
                                className="flex items-center justify-between gap-3 py-2"
                            >
                                <div className="min-w-0">
                                    <p className="text-sm text-base-content truncate">
                                        {item.title || `#${item.id}`}
                                    </p>
                                    <p className="text-xs text-base-content/50">
                                        {originLabels[item.type]}
                                        {item.scheduledAt &&
                                            ` · ${new Date(
                                                item.scheduledAt
                                            ).toLocaleString('pt-BR', {
                                                dateStyle: 'short',
                                                timeStyle: 'short',
                                            })}`}
                                    </p>
                                </div>
                                <button
                                    onClick={() =>
                                        navigate(
                                            `${ORIGIN_ROUTES[item.type]}/${item.id}`
                                        )
                                    }
                                    className="btn btn-ghost btn-sm gap-2 shrink-0"
                                >
                                    <MdOpenInNew className="text-base" />
                                    Abrir
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Origem: agendamento/pedido que gerou a cobrança */}
            {origins.length <= 1 && origin && (
                <div className="bg-base-100 rounded-xl border border-base-300/50 p-4 flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center shrink-0">
                            <MdEventNote className="text-xl text-secondary" />
                        </div>
                        <div className="min-w-0">
                            <p className="text-xs text-base-content/50">
                                {originLabels[origin.type]}
                            </p>
                            <p className="font-medium text-base-content truncate">
                                {origin.title || `#${origin.id}`}
                            </p>
                            {origin.scheduledAt && (
                                <p className="text-xs text-base-content/60">
                                    {new Date(
                                        origin.scheduledAt
                                    ).toLocaleString('pt-BR', {
                                        dateStyle: 'short',
                                        timeStyle: 'short',
                                    })}
                                </p>
                            )}
                        </div>
                    </div>
                    <button
                        onClick={() =>
                            navigate(
                                `${ORIGIN_ROUTES[origin.type]}/${origin.id}`
                            )
                        }
                        className="btn btn-outline btn-secondary gap-2"
                    >
                        <MdOpenInNew className="text-lg" />
                        Abrir {originLabels[origin.type].toLowerCase()}
                    </button>
                </div>
            )}

            {/* Vínculo com a cobrança original / complementos.

                Quando o valor de um atendimento pago sobe, a diferença vira uma cobrança
                COMPLEMENTAR: a original não é alterada (o cliente pagou aquilo mesmo). Sem
                este bloco, o complemento aparecia como um lançamento solto — ninguém sabia
                de onde veio nem que o atendimento tem duas cobranças. */}
            {/* Cobrança FILHA (complemento/parcela): link para a cobrança âncora. */}
            {billing.parentBilling && (
                <div className="bg-base-100 rounded-xl border border-base-300/50 p-4">
                    <p className="text-xs text-base-content/50 mb-3">
                        Esta é uma cobrança complementar
                    </p>
                    <button
                        onClick={() =>
                            navigate(
                                `/financial/billings/${billing.parentBilling!.id}`
                            )
                        }
                        className="flex items-center justify-between gap-4 w-full rounded-lg border border-base-300/50 px-3 py-2 text-left hover:bg-base-200 transition-colors"
                    >
                        <div className="min-w-0">
                            <p className="font-medium text-base-content">
                                {`Cobrança principal #${billing.parentBilling.id}`}
                            </p>
                            <p className="text-xs text-base-content/60 truncate">
                                {formatCurrency(billing.parentBilling.amount)} ·{' '}
                                {STATUS_LABELS[billing.parentBilling.status]}
                            </p>
                        </div>
                        <MdOpenInNew className="text-lg text-secondary shrink-0" />
                    </button>
                </div>
            )}

            {/* Solicitação já em fila: o botão de baixa some, e o usuário precisa
                saber por quê — senão clica de novo por outro caminho. */}
            {isAwaitingApproval && (
                <div className="alert alert-warning items-start gap-3">
                    <MdHourglassTop className="text-2xl shrink-0" />
                    <div>
                        <p className="font-semibold">
                            {pendingApproval?.title ??
                                'Aguardando aprovação de um operador financeiro'}
                        </p>
                        <p className="text-sm mt-1">
                            {pendingApproval?.description ??
                                'Já existe uma solicitação em fila para esta cobrança.'}{' '}
                            Enquanto ela não for decidida, nenhuma nova ação
                            pode ser disparada.
                        </p>
                        {(pendingApproval?.requestedBy ||
                            pendingApproval?.requestedAt) && (
                            <p className="text-xs mt-1 opacity-80">
                                Solicitado
                                {pendingApproval.requestedBy
                                    ? ` por ${pendingApproval.requestedBy}`
                                    : ''}
                                {pendingApproval.requestedAt
                                    ? ` em ${formatDate(pendingApproval.requestedAt)}`
                                    : ''}
                            </p>
                        )}
                        <button
                            onClick={() =>
                                navigate(
                                    pendingApproval?.href ??
                                        '/financial/bills-approvals'
                                )
                            }
                            className="btn btn-sm btn-outline mt-3"
                        >
                            {pendingApproval?.linkLabel ??
                                'Ver aprovações financeiras'}
                        </button>
                    </div>
                </div>
            )}

            {/* Conciliação e referência.

                Status, valor e método saíram daqui: o card "Cobrança principal" logo
                abaixo mostra os três, e ler o mesmo dado duas vezes na mesma rolagem
                faz o operador procurar a diferença entre eles. Ficam só os dados que
                o card não tem. */}
            {(billing.isReconciled ||
                billing.reconciliationPending ||
                billing.referenceCode) && (
                <div className="flex items-center gap-3 flex-wrap">
                    {billing.isReconciled && (
                        <span className="badge badge-success badge-outline">
                            Conciliado
                        </span>
                    )}
                    {billing.reconciliationPending && (
                        <span className="badge badge-warning badge-outline">
                            Em Análise
                        </span>
                    )}
                    {billing.referenceCode && (
                        <span className="badge badge-ghost">
                            Ref: {billing.referenceCode}
                        </span>
                    )}
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Cliente ou Fornecedor — vem PRIMEIRO e ocupa a largura toda: os cards
                    de cobrança logo abaixo falam do dinheiro, e quem paga se lê antes. */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 p-6 lg:col-span-2">
                    <h2 className="text-lg font-semibold text-base-content mb-4 flex items-center gap-2">
                        {isPayable ? (
                            <MdStore className="text-xl" />
                        ) : (
                            <MdPerson className="text-xl" />
                        )}
                        {isPayable
                            ? billing.supplier || billing.supplierName
                                ? 'Fornecedor'
                                : (billing as any).user
                                  ? 'Usuário'
                                  : (billing as any).employee
                                    ? 'Funcionário'
                                    : billing.partner
                                      ? 'Parceiro'
                                      : billing.hospital
                                        ? 'Hospital'
                                        : billing.professional
                                          ? 'Profissional'
                                          : billing.client
                                            ? 'Cliente'
                                            : 'Destinatário'
                            : isHealthPlanPayer
                              ? 'Convênio (pagador)'
                              : billing.client
                                ? 'Cliente'
                                : billing.partner
                                  ? 'Parceiro'
                                  : 'Destinatário'}
                    </h2>
                    <div className="space-y-3">
                        {isPayable ? (
                            <>
                                <div>
                                    <p className="text-xs text-base-content/50">
                                        Nome
                                    </p>
                                    <p className="font-medium">
                                        {billing.supplier?.name ||
                                            billing.supplierName ||
                                            (billing as any).user?.name ||
                                            (billing as any).employee?.name ||
                                            billing.partner?.name ||
                                            billing.partner?.tradingName ||
                                            billing.hospital?.tradingName ||
                                            billing.hospital?.name ||
                                            billing.professional?.name ||
                                            billing.client?.name ||
                                            '-'}
                                    </p>
                                </div>
                                {billing.supplier?.tradingName && (
                                    <div>
                                        <p className="text-xs text-base-content/50">
                                            Nome Fantasia
                                        </p>
                                        <p className="font-medium">
                                            {billing.supplier.tradingName}
                                        </p>
                                    </div>
                                )}
                                {billing.supplier?.cnpj && (
                                    <div>
                                        <p className="text-xs text-base-content/50">
                                            CNPJ
                                        </p>
                                        <p className="font-medium">
                                            {billing.supplier.cnpj}
                                        </p>
                                    </div>
                                )}
                                {(billing as any).user?.email && (
                                    <div>
                                        <p className="text-xs text-base-content/50">
                                            Email
                                        </p>
                                        <p className="font-medium">
                                            {(billing as any).user.email}
                                        </p>
                                    </div>
                                )}
                                {(billing as any).employee?.cpf && (
                                    <div>
                                        <p className="text-xs text-base-content/50">
                                            CPF
                                        </p>
                                        <p className="font-medium">
                                            {(billing as any).employee.cpf}
                                        </p>
                                    </div>
                                )}
                                {billing.category && (
                                    <div>
                                        <p className="text-xs text-base-content/50">
                                            Categoria
                                        </p>
                                        <p className="font-medium">
                                            {billingCategoryLabel(
                                                billing.category,
                                                serviceLabels
                                            )}
                                        </p>
                                    </div>
                                )}
                                {(billing.hospital &&
                                    !(
                                        billing.supplier || billing.supplierName
                                    ) &&
                                    (billing as any).user) ||
                                (billing as any).employee ||
                                billing.partner ||
                                billing.professional
                                    ? null
                                    : billing.hospital && (
                                          <div className="pt-3 mt-3 border-t border-base-300/50">
                                              <p className="text-xs text-base-content/50 flex items-center gap-1">
                                                  <MdLocalHospital className="text-base" />
                                                  Hospital vinculado
                                              </p>
                                              <p className="font-medium">
                                                  {billing.hospital
                                                      .tradingName ||
                                                      billing.hospital.name}
                                              </p>
                                              {billing.hospital.cnpj && (
                                                  <p className="text-xs text-base-content/60 mt-1">
                                                      CNPJ:{' '}
                                                      {billing.hospital.cnpj}
                                                  </p>
                                              )}
                                          </div>
                                      )}
                            </>
                        ) : (
                            <>
                                {/* Pagador convênio: a operadora vem primeiro, e o
                                    paciente logo abaixo como referência do atendimento —
                                    é a leitura que a tela precisa dar, já que a cobrança
                                    não tem link nem cobra o paciente. */}
                                {isHealthPlanPayer && (
                                    <div className="rounded-lg bg-secondary/10 border border-secondary/20 p-4 space-y-2">
                                        <div>
                                            <p className="text-xs text-base-content/50">
                                                Convênio
                                            </p>
                                            <p className="font-semibold">
                                                {billing.healthPlan?.name ||
                                                    `Convênio #${billing.healthPlanId}`}
                                            </p>
                                        </div>
                                        {billing.healthPlan?.ansCode && (
                                            <div>
                                                <p className="text-xs text-base-content/50">
                                                    Registro ANS
                                                </p>
                                                <p className="font-medium">
                                                    {billing.healthPlan.ansCode}
                                                </p>
                                            </div>
                                        )}
                                        {billing.healthPlan?.cnpj && (
                                            <div>
                                                <p className="text-xs text-base-content/50">
                                                    CNPJ
                                                </p>
                                                <p className="font-medium">
                                                    {billing.healthPlan.cnpj}
                                                </p>
                                            </div>
                                        )}
                                        <p className="text-xs text-base-content/60 pt-1">
                                            Cobrança faturada ao convênio. O
                                            paciente não recebe link de
                                            pagamento nem entra na régua de
                                            cobrança.
                                        </p>
                                    </div>
                                )}
                                <div>
                                    <p className="text-xs text-base-content/50">
                                        {isHealthPlanPayer
                                            ? 'Paciente'
                                            : 'Nome'}
                                    </p>
                                    {/* Nome do cliente leva ao cadastro dele — mesmo atalho
                                        das telas de atendimento. Só quando é cliente (parceiro
                                        não tem essa tela). */}
                                    {billing.client ? (
                                        <button
                                            onClick={() =>
                                                navigate(
                                                    `/person/clients/${billing.client!.id}`
                                                )
                                            }
                                            className="font-medium link link-hover text-left inline-flex items-center gap-1"
                                            title="Ver cadastro do cliente"
                                        >
                                            {billing.client.name}
                                            <MdOpenInNew className="text-sm text-base-content/40" />
                                        </button>
                                    ) : (
                                        <p className="font-medium">
                                            {billing.partner?.name ||
                                                billing.partner?.tradingName ||
                                                '-'}
                                        </p>
                                    )}
                                </div>
                                {billing.client?.email && (
                                    <div>
                                        <p className="text-xs text-base-content/50">
                                            Email
                                        </p>
                                        <a
                                            href={`mailto:${billing.client.email}`}
                                            className="text-secondary hover:underline"
                                        >
                                            {billing.client.email}
                                        </a>
                                    </div>
                                )}
                                {billing.client?.whatsAppNumber && (
                                    <div>
                                        <p className="text-xs text-base-content/50">
                                            WhatsApp
                                        </p>
                                        <p className="font-medium">
                                            {formatPhoneNumber(
                                                billing.client.whatsAppNumber
                                            )}
                                        </p>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>

                {/* Cobranças do atendimento, TODAS no mesmo card.

                    Antes a âncora era descrita pelos cards da página enquanto as filhas
                    apareciam lá em cima num card de visual próprio — duas linguagens para
                    a mesma coisa, e a mais chamativa era a secundária. Agora âncora e
                    complementos usam o `BillingInfoSection`, em sequência e logo abaixo de
                    quem paga. "Valores e Datas" continua adiante: só ele detalha taxas,
                    desconto, juros, cancelamento e parcela. */}
                <div className="lg:col-span-2 flex flex-col gap-6">
                    <BillingInfoSection
                        billing={billing as any}
                        canUpdate={canUpdate}
                        title={
                            hasChildBillings ? 'Cobrança principal' : 'Cobrança'
                        }
                        // O link levaria para esta mesma página.
                        hideDetailsLink
                        // QR e link já têm cards próprios nesta página.
                        hidePaymentShare
                        onBillingChanged={loadBilling}
                        onMarkAsPaid={handleMarkAsPaidFromCard}
                    />
                    {/* Filha (complemento/parcela): a âncora fica em outra página, então
                        aqui não há irmãs para listar. */}
                    {!billing.parentBilling && (
                        <>
                            {/* Formas de pagamento e complementos são as duas filhas
                                da mesma relação, e chamar as duas de "complementar"
                                mente na conta a pagar dividida: aquela parte em
                                dinheiro não complementa nada, ela É parte do valor. */}
                            <BillingChildrenSections
                                adjustments={paymentSplitChildren}
                                onReload={loadBilling}
                                canUpdate={canUpdate}
                                childTitle="Forma de pagamento"
                            />
                            <BillingChildrenSections
                                adjustments={complementChildren}
                                onReload={loadBilling}
                                canUpdate={canUpdate}
                                childTitle="Cobrança complementar"
                            />
                        </>
                    )}
                </div>

                {/* Solicitação agrupada: a composição.
                    Sem ela, a tela mostrava só um total de período inteiro — e
                    conferir um lote é justamente ver de que atendimentos ele veio. */}
                {isPayoutBatch && (
                    <div className="bg-base-100 rounded-xl border border-base-300/50 p-6">
                        <div className="flex items-start justify-between gap-3 mb-4">
                            <h2 className="text-lg font-semibold text-base-content flex items-center gap-2">
                                <MdReceipt className="text-xl" />
                                Repasses agrupados
                                {batchSummary.total > 0 && (
                                    <span className="badge badge-sm badge-ghost">
                                        {batchSummary.total}
                                    </span>
                                )}
                            </h2>
                            {awaitingBatchSetup ? (
                                <span
                                    className="badge badge-warning badge-sm"
                                    title="Ainda fora da fila: defina vencimento e formas de pagamento e envie para aprovação"
                                >
                                    Em configuração
                                </span>
                            ) : rejectedBatch ? (
                                <span
                                    className="badge badge-error badge-sm"
                                    title={
                                        (billing as any).rejectedReason ||
                                        'Recusada pelo aprovador'
                                    }
                                >
                                    Recusada
                                </span>
                            ) : isAwaitingApproval ? (
                                <span className="badge badge-info badge-sm">
                                    Aguardando aprovação
                                </span>
                            ) : null}
                        </div>

                        {rejectedBatch && (
                            <div className="alert alert-error text-sm mb-4">
                                <MdWarning className="text-lg" />
                                <span>
                                    Recusada pelo aprovador
                                    {(billing as any).rejectedReason
                                        ? `: ${(billing as any).rejectedReason}`
                                        : '.'}{' '}
                                    Os repasses voltaram a ficar disponíveis:
                                    corrija o que motivou a recusa e gere um
                                    novo agrupamento em{' '}
                                    <strong>Relatórios → Lançamentos</strong>.
                                </span>
                            </div>
                        )}

                        {awaitingBatchSetup && (
                            <div className="alert alert-warning text-sm mb-4">
                                <MdWarning className="text-lg" />
                                <span>
                                    Esta solicitação ainda <strong>não</strong>{' '}
                                    está na fila de aprovação. Confira o
                                    vencimento e as formas de pagamento e clique
                                    em <strong>Enviar para aprovação</strong> —
                                    é nesse momento que o demonstrativo é
                                    gerado.
                                </span>
                            </div>
                        )}

                        <PayoutBatchItemsList
                            batchId={parseInt(id!, 10)}
                            onSummary={handleBatchSummary}
                        />

                        {batchSummary.total > 0 && (
                            <div className="flex items-center justify-between gap-3 mt-4 px-4 py-3 rounded-xl bg-base-200/60">
                                <span className="text-sm">
                                    {batchSummary.total}{' '}
                                    {batchSummary.total === 1
                                        ? 'repasse'
                                        : 'repasses'}
                                </span>
                                <span className="font-bold">
                                    {formatCurrency(batchSummary.totalAmount)}
                                </span>
                            </div>
                        )}

                        <p className="text-xs text-base-content/50 mt-3">
                            Ao ser paga, todos os lançamentos acima recebem
                            baixa automaticamente. Para tirar um deles da
                            solicitação, use a edição — só é possível antes da
                            aprovação.
                        </p>
                    </div>
                )}

                {/* Valores */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 p-6">
                    <h2 className="text-lg font-semibold text-base-content mb-4 flex items-center gap-2">
                        <MdAttachMoney className="text-xl" />
                        Valores e Datas
                    </h2>
                    <div className="space-y-3">
                        <div className="flex justify-between">
                            <span className="text-base-content/60">Valor</span>
                            {/* Coberto por convênio: o `amount` é zero de propósito
                                (quem cobra o convênio é a fatura do período), e
                                mostrar "R$ 0,00" faria a cobrança parecer sem valor. */}
                            <span className="font-bold">
                                {formatCurrency(
                                    isHealthPlanPayer
                                        ? (billing.invoicedAmount ?? 0)
                                        : billing.amount
                                )}
                            </span>
                        </div>
                        {isHealthPlanPayer && (
                            <p className="text-xs leading-tight text-base-content/60">
                                Faturado ao convênio. Nada é cobrado do
                                paciente, e o valor entra na fatura do período.
                            </p>
                        )}
                        {billing.fees != null &&
                            billing.fees.length > 0 &&
                            billing.fees.map((fee, idx) => (
                                <div
                                    key={idx}
                                    className="flex justify-between text-sm"
                                >
                                    <span className="text-base-content/50">
                                        {fee.label}
                                        {fee.mode === 'PERCENT'
                                            ? ` (${fee.rate}%)`
                                            : ' (fixa)'}{' '}
                                        — incluída no valor
                                    </span>
                                    <span className="text-base-content/60">
                                        {formatCurrency(fee.feeAmount)}
                                    </span>
                                </div>
                            ))}
                        {billing.discount != null && billing.discount > 0 && (
                            <div className="flex justify-between">
                                <span className="text-base-content/60">
                                    Desconto
                                </span>
                                <span className="text-success">
                                    - {formatCurrency(billing.discount)}
                                </span>
                            </div>
                        )}
                        {billing.interest != null && billing.interest > 0 && (
                            <div className="flex justify-between">
                                <span className="text-base-content/60">
                                    Juros/Multa
                                </span>
                                <span className="text-error">
                                    + {formatCurrency(billing.interest)}
                                </span>
                            </div>
                        )}
                        {billing.paidAmount != null && (
                            <div className="flex justify-between">
                                <span className="text-base-content/60">
                                    Valor Pago
                                </span>
                                <span className="font-bold text-success">
                                    {formatCurrency(billing.paidAmount)}
                                </span>
                            </div>
                        )}
                        <div className="divider my-1"></div>
                        <div className="flex justify-between">
                            <span className="text-base-content/60">
                                Vencimento
                            </span>
                            <span className="font-medium">
                                {formatDate(billing.dueDate)}
                            </span>
                        </div>
                        {billing.paidAt && (
                            <div className="flex justify-between">
                                <span className="text-base-content/60">
                                    Pago em
                                </span>
                                <span className="font-medium">
                                    {formatDateTime(billing.paidAt)}
                                </span>
                            </div>
                        )}
                        {billing.cancelledAt && (
                            <div className="flex justify-between">
                                <span className="text-base-content/60">
                                    Cancelado em
                                </span>
                                <span className="font-medium">
                                    {formatDateTime(billing.cancelledAt)}
                                </span>
                            </div>
                        )}
                        {billing.installmentNumber &&
                            billing.installmentTotal && (
                                <div className="flex justify-between">
                                    <span className="text-base-content/60">
                                        Parcela
                                    </span>
                                    <span className="font-medium">
                                        {billing.installmentNumber}/
                                        {billing.installmentTotal}
                                    </span>
                                </div>
                            )}
                    </div>
                </div>

                {/* Repasses (snapshot do agendamento) — apenas linhas com valor > 0 sao mostradas.
                    Se nada houver, o card inteiro nao renderiza. */}
                {billing.type === 'RECEIVABLE' &&
                    (() => {
                        if (!canDelete) {
                            return null
                        }

                        // O repasse mora nas FATIAS — uma por atendimento. Os
                        // campos escalares do Billing foram removidos (fase 4 de
                        // docs/billing-payout-share.md); sem fatia não há repasse
                        // a fazer, e o card não aparece.
                        const shares = (billing as any).payoutShares as
                            | PayoutShareDetail[]
                            | undefined
                        if (!shares?.length) return null

                        return (
                            <SharedPayoutCard
                                billingId={billing.id}
                                canSplitByItem={canUpdate}
                                onPayoutsChanged={loadPlannedPayouts}
                                shares={shares}
                                planned={plannedPayouts}
                                /* Coberto por convênio: o `amount` é zero de
                                   propósito, e usá-lo aqui daria margem NEGATIVA
                                   (receita zero menos o repasse real ao parceiro),
                                   como se a venda tivesse dado prejuízo. A receita
                                   é o valor faturado ao convênio. */
                                total={
                                    isHealthPlanPayer
                                        ? (billing.invoicedAmount ?? 0)
                                        : billing.amount
                                }
                                origins={origins}
                                coveredBy={
                                    isHealthPlanPayer
                                        ? billing.healthPlan?.name || 'convênio'
                                        : null
                                }
                            />
                        )
                    })()}

                {/* Envolvidos no atendimento (parceiro, profissional, hospital). Visível a
                    TODOS os operadores — diferente do bloco de "Repasses", que é só company. */}
                {(billing.partner ||
                    billing.professional ||
                    billing.hospital) && (
                    <div className="bg-base-100 rounded-xl border border-base-300/50 p-6">
                        <div className="space-y-3">
                            {billing.partner && (
                                <div>
                                    <p className="text-xs text-base-content/50">
                                        Parceiro
                                    </p>
                                    <p className="font-medium">
                                        {billing.partner.tradingName ||
                                            billing.partner.name}
                                    </p>
                                </div>
                            )}
                            {billing.professional && (
                                <div>
                                    <p className="text-xs text-base-content/50">
                                        Profissional
                                    </p>
                                    <p className="font-medium">
                                        {billing.professional.name}
                                    </p>
                                    {billing.professional.email && (
                                        <p className="text-sm text-base-content/60">
                                            {billing.professional.email}
                                        </p>
                                    )}
                                </div>
                            )}
                            {billing.hospital && (
                                <div>
                                    <p className="text-xs text-base-content/50">
                                        Hospital
                                    </p>
                                    <p className="font-medium">
                                        {billing.hospital.tradingName ||
                                            billing.hospital.name}
                                    </p>
                                    {billing.hospital.cnpj && (
                                        <p className="text-sm text-base-content/60">
                                            {billing.hospital.cnpj}
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Dados PIX - apenas RECEIVABLE */}
                {!isPayable &&
                    (billing.pixKey ||
                        billing.pixCopyPaste ||
                        billing.pixQrCode ||
                        billing.pixQrCodeBase64) && (
                        <div className="bg-base-100 rounded-xl border border-base-300/50 p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-lg font-semibold text-base-content flex items-center gap-2">
                                    <MdQrCode className="text-xl" />
                                    Dados PIX
                                </h2>
                                {canUpdate && (
                                    <button
                                        onClick={handleSendEmail}
                                        disabled={sendingEmail}
                                        className="btn btn-sm btn-outline btn-secondary gap-1"
                                    >
                                        {sendingEmail ? (
                                            <span className="loading loading-spinner loading-xs"></span>
                                        ) : (
                                            <MdEmail className="text-base" />
                                        )}
                                        Enviar por Email
                                    </button>
                                )}
                            </div>
                            <div className="space-y-3">
                                {billing.pixQrCodeBase64 && (
                                    <div className="flex justify-center">
                                        <img
                                            src={`data:image/png;base64,${billing.pixQrCodeBase64}`}
                                            alt="QR Code PIX"
                                            className="w-48 h-48 rounded-lg border border-base-300"
                                        />
                                    </div>
                                )}
                                {sandboxPayAvailable &&
                                    canPayInSandbox &&
                                    canUpdate && (
                                        <div className="flex flex-col items-center gap-1">
                                            <button
                                                onClick={handlePayInSandbox}
                                                disabled={payingInSandbox}
                                                className="btn btn-sm btn-warning gap-1"
                                            >
                                                {payingInSandbox ? (
                                                    <span className="loading loading-spinner loading-xs"></span>
                                                ) : (
                                                    <MdPayment className="text-base" />
                                                )}
                                                Pagar (sandbox)
                                            </button>
                                            <p className="text-xs text-base-content/50">
                                                Ambiente de testes do gateway: paga
                                                com o saldo de teste da conta.
                                            </p>
                                        </div>
                                    )}
                                {billing.pixKey && (
                                    <div>
                                        <p className="text-xs text-base-content/50">
                                            Chave PIX
                                        </p>
                                        <p className="font-medium break-all">
                                            {billing.pixKey}
                                        </p>
                                    </div>
                                )}
                                {billing.pixCopyPaste && (
                                    <div>
                                        <p className="text-xs text-base-content/50">
                                            Copia e Cola
                                        </p>
                                        <div className="flex gap-2 items-start">
                                            <p className="font-mono text-sm break-all bg-base-200 p-2 rounded flex-1">
                                                {billing.pixCopyPaste}
                                            </p>
                                            <button
                                                onClick={() =>
                                                    handleCopy(
                                                        billing.pixCopyPaste!,
                                                        'pix'
                                                    )
                                                }
                                                className="btn btn-sm btn-square btn-ghost"
                                                title="Copiar"
                                            >
                                                {copiedField === 'pix' ? (
                                                    <MdCheckCircle className="text-success text-base" />
                                                ) : (
                                                    <MdContentCopy className="text-base" />
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                )}
                                {billing.pixExpirationDate && (
                                    <div>
                                        <p className="text-xs text-base-content/50">
                                            Expiracao PIX
                                        </p>
                                        <p className="font-medium">
                                            {formatDate(
                                                billing.pixExpirationDate
                                            )}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                {/* Dados Boleto - apenas RECEIVABLE */}
                {!isPayable &&
                    (billing.bankSlipUrl ||
                        billing.nossoNumero ||
                        billing.barcode) && (
                        <div className="bg-base-100 rounded-xl border border-base-300/50 p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-lg font-semibold text-base-content flex items-center gap-2">
                                    <MdPayment className="text-xl" />
                                    Dados do Boleto
                                </h2>
                                {canUpdate && (
                                    <button
                                        onClick={handleSendEmail}
                                        disabled={sendingEmail}
                                        className="btn btn-sm btn-outline btn-secondary gap-1"
                                    >
                                        {sendingEmail ? (
                                            <span className="loading loading-spinner loading-xs"></span>
                                        ) : (
                                            <MdEmail className="text-base" />
                                        )}
                                        Enviar por Email
                                    </button>
                                )}
                            </div>
                            <div className="space-y-3">
                                {billing.bankSlipUrl && (
                                    <div>
                                        <p className="text-xs text-base-content/50">
                                            URL do Boleto
                                        </p>
                                        <div className="flex gap-2 items-center">
                                            <a
                                                href={billing.bankSlipUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-secondary hover:underline break-all flex-1"
                                            >
                                                {billing.bankSlipUrl}
                                            </a>
                                            <button
                                                onClick={() =>
                                                    handleCopy(
                                                        billing.bankSlipUrl!,
                                                        'boleto-url'
                                                    )
                                                }
                                                className="btn btn-sm btn-square btn-ghost"
                                                title="Copiar"
                                            >
                                                {copiedField ===
                                                'boleto-url' ? (
                                                    <MdCheckCircle className="text-success text-base" />
                                                ) : (
                                                    <MdContentCopy className="text-base" />
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                )}
                                {billing.nossoNumero && (
                                    <div>
                                        <p className="text-xs text-base-content/50">
                                            Nosso Numero
                                        </p>
                                        <p className="font-medium">
                                            {billing.nossoNumero}
                                        </p>
                                    </div>
                                )}
                                {billing.barcode && (
                                    <div>
                                        <p className="text-xs text-base-content/50">
                                            Codigo de Barras
                                        </p>
                                        <p className="font-mono text-sm break-all">
                                            {billing.barcode}
                                        </p>
                                    </div>
                                )}
                                {billing.digitableLine && (
                                    <div>
                                        <p className="text-xs text-base-content/50">
                                            Linha Digitavel
                                        </p>
                                        <div className="flex gap-2 items-start">
                                            <p className="font-mono text-sm break-all bg-base-200 p-2 rounded flex-1">
                                                {billing.digitableLine}
                                            </p>
                                            <button
                                                onClick={() =>
                                                    handleCopy(
                                                        billing.digitableLine!,
                                                        'digitavel'
                                                    )
                                                }
                                                className="btn btn-sm btn-square btn-ghost"
                                                title="Copiar"
                                            >
                                                {copiedField === 'digitavel' ? (
                                                    <MdCheckCircle className="text-success text-base" />
                                                ) : (
                                                    <MdContentCopy className="text-base" />
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                {/* Dados do Terminal - RECEIVABLE pago em maquininha fisica */}
                {!isPayable && billing.paymentMethod === 'TERMINAL' && (
                    <div className="bg-base-100 rounded-xl border border-base-300/50 p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-lg font-semibold text-base-content flex items-center gap-2">
                                <MdPayment className="text-xl" />
                                Dados do Terminal
                            </h2>
                        </div>
                        <div className="alert alert-info text-sm mb-3">
                            <span>
                                Pagamento offline confirmado em maquininha
                                física. Esta cobrança não é processada por
                                gateway externo — o valor já foi recebido no
                                terminal.
                            </span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                                <p className="text-xs text-base-content/50">
                                    Método
                                </p>
                                <p className="font-medium">
                                    Maquininha (terminal)
                                </p>
                            </div>
                            {billing.referenceCode && (
                                <div>
                                    <p className="text-xs text-base-content/50">
                                        Código / NSU / Autorização
                                    </p>
                                    <p className="font-mono text-sm">
                                        {billing.referenceCode}
                                    </p>
                                </div>
                            )}
                            <div>
                                <p className="text-xs text-base-content/50">
                                    Status
                                </p>
                                <p className="font-medium">
                                    {STATUS_LABELS[billing.status]}
                                </p>
                            </div>
                            {billing.paidAt && (
                                <div>
                                    <p className="text-xs text-base-content/50">
                                        Pago em
                                    </p>
                                    <p className="font-medium">
                                        {formatDateTime(billing.paidAt)}
                                    </p>
                                </div>
                            )}
                            {billing.paidAmount != null && (
                                <div>
                                    <p className="text-xs text-base-content/50">
                                        Valor pago
                                    </p>
                                    <p className="font-mono">
                                        {formatCurrency(billing.paidAmount)}
                                    </p>
                                </div>
                            )}
                            {billing.reconciledBy?.name && (
                                <div>
                                    <p className="text-xs text-base-content/50">
                                        Confirmado por
                                    </p>
                                    <p className="font-medium">
                                        {billing.reconciledBy.name}
                                    </p>
                                </div>
                            )}
                            {billing.reconciliationNotes && (
                                <div className="md:col-span-2">
                                    <p className="text-xs text-base-content/50">
                                        Observações
                                    </p>
                                    <p className="text-sm whitespace-pre-wrap">
                                        {billing.reconciliationNotes}
                                    </p>
                                </div>
                            )}
                            {!billing.referenceCode &&
                                !billing.paidAt &&
                                (billing.status === 'PENDING' ||
                                    billing.status === 'OVERDUE') && (
                                    <div className="md:col-span-2">
                                        <p className="text-xs text-base-content/60 italic">
                                            Use o botão "Pago" no topo para
                                            registrar o pagamento — você poderá
                                            informar código/NSU e observações.
                                        </p>
                                    </div>
                                )}
                        </div>
                    </div>
                )}

                {/* Provider de pagamento - apenas RECEIVABLE. O link de checkout
                    só conta como motivo para exibir o card nos métodos online
                    (senão dinheiro/maquininha mostrariam um card "Pagamento"
                    vazio, já que todo billing tem publicToken). */}
                {!isPayable &&
                    (billing.provider ||
                        billing.paymentLink ||
                        (hasCheckoutLink && getCheckoutUrl())) && (
                        <div className="bg-base-100 rounded-xl border border-base-300/50 p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-lg font-semibold text-base-content flex items-center gap-2">
                                    <MdLink className="text-xl" />
                                    Pagamento
                                </h2>
                                {canUpdate && billing.providerPaymentId && (
                                    <button
                                        onClick={handleSendEmail}
                                        disabled={sendingEmail}
                                        className="btn btn-sm btn-outline btn-secondary gap-1"
                                    >
                                        {sendingEmail ? (
                                            <span className="loading loading-spinner loading-xs"></span>
                                        ) : (
                                            <MdEmail className="text-base" />
                                        )}
                                        Enviar por Email
                                    </button>
                                )}
                            </div>
                            <div className="space-y-3">
                                {billing.provider && (
                                    <div>
                                        <p className="text-xs text-base-content/50">
                                            Provider
                                        </p>
                                        <p className="font-medium capitalize">
                                            {billing.provider}
                                        </p>
                                    </div>
                                )}
                                {billing.providerPaymentId && (
                                    <div>
                                        <p className="text-xs text-base-content/50">
                                            ID do Pagamento
                                        </p>
                                        <p className="font-mono text-sm">
                                            {billing.providerPaymentId}
                                        </p>
                                    </div>
                                )}
                                {billing.providerOrderId && (
                                    <div>
                                        <p className="text-xs text-base-content/50">
                                            ID do Pedido
                                        </p>
                                        <p className="font-mono text-sm">
                                            {billing.providerOrderId}
                                        </p>
                                    </div>
                                )}
                                {/*billing.paymentLink && (
                                <div>
                                    <p className="text-xs text-base-content/50">Link de Pagamento</p>
                                    <div className="flex gap-2 items-center">
                                        <a
                                            href={billing.paymentLink}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-secondary hover:underline break-all flex-1"
                                        >
                                            {billing.paymentLink}
                                        </a>
                                        <button
                                            onClick={() => handleCopy(billing.paymentLink!, 'payment-link')}
                                            className="btn btn-sm btn-square btn-ghost"
                                            title="Copiar"
                                        >
                                            {copiedField === 'payment-link' ? (
                                                <MdCheckCircle className="text-success text-base" />
                                            ) : (
                                                <MdContentCopy className="text-base" />
                                            )}
                                        </button>
                                    </div>
                                </div>
                            )*/}
                                {getCheckoutUrl() &&
                                    hasCheckoutLink &&
                                    (billing.status === 'PENDING' ||
                                        billing.status === 'OVERDUE') && (
                                        <div>
                                            <p className="text-xs text-base-content/50">
                                                Link de Checkout
                                            </p>
                                            <div className="flex gap-2 items-center">
                                                <a
                                                    href={getCheckoutUrl()!}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-secondary hover:underline break-all flex-1 flex items-center gap-1"
                                                >
                                                    {getCheckoutUrl()}
                                                    <MdOpenInNew className="text-sm flex-shrink-0" />
                                                </a>
                                                <button
                                                    onClick={() =>
                                                        handleCopy(
                                                            getCheckoutUrl()!,
                                                            'checkout-link'
                                                        )
                                                    }
                                                    className="btn btn-sm btn-square btn-ghost"
                                                    title="Copiar"
                                                >
                                                    {copiedField ===
                                                    'checkout-link' ? (
                                                        <MdCheckCircle className="text-success text-base" />
                                                    ) : (
                                                        <MdContentCopy className="text-base" />
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    )}
                            </div>
                        </div>
                    )}

                {/* Conciliação Bancária */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-semibold text-base-content flex items-center gap-2">
                            <MdAccountBalance className="text-xl" />
                            Conciliação Bancária
                        </h2>
                        {/* Automática (webhook BaaS + extrato). Vínculo manual
                            cobre créditos sem referência que a automação não casou. */}
                        {canUpdate && !isPayable && (
                            <div className="flex items-center gap-2">
                                {linkedLedgerId ? (
                                    <button
                                        onClick={handleUnlinkLedger}
                                        disabled={linking}
                                        className="btn btn-sm btn-outline btn-warning gap-1"
                                    >
                                        {linking ? (
                                            <span className="loading loading-spinner loading-xs"></span>
                                        ) : (
                                            <MdLinkOff className="text-base" />
                                        )}
                                        Desvincular
                                    </button>
                                ) : (
                                    (billing.status === 'PENDING' ||
                                        billing.status === 'OVERDUE') && (
                                        <button
                                            onClick={openLinkModal}
                                            className="btn btn-sm btn-outline btn-secondary gap-1"
                                        >
                                            <MdLink className="text-base" />
                                            Vincular movimentação
                                        </button>
                                    )
                                )}
                            </div>
                        )}
                    </div>
                    <div className="space-y-3">
                        <div className="flex justify-between">
                            <span className="text-base-content/60">Status</span>
                            <span className="font-medium">
                                {billing.isReconciled ? (
                                    <span className="text-success">
                                        Conciliado
                                    </span>
                                ) : billing.reconciliationPending ? (
                                    <span className="text-warning">
                                        Em Análise (Divergência)
                                    </span>
                                ) : (
                                    <span className="text-base-content/50">
                                        Não conciliado
                                    </span>
                                )}
                            </span>
                        </div>
                        {billing.reconciledAt && (
                            <div className="flex justify-between">
                                <span className="text-base-content/60">
                                    Data da Conciliação
                                </span>
                                <span className="font-medium">
                                    {formatDateTime(billing.reconciledAt)}
                                </span>
                            </div>
                        )}
                        {billing.reconciledBy && (
                            <div className="flex justify-between">
                                <span className="text-base-content/60">
                                    Conciliado por
                                </span>
                                <span className="font-medium">
                                    {billing.reconciledBy.name}
                                </span>
                            </div>
                        )}
                        {billing.reconciliationNotes && (
                            <div>
                                <p className="text-xs text-base-content/50 mb-1">
                                    Observações da Conciliação
                                </p>
                                <p className="text-base-content bg-base-200/50 p-3 rounded-lg whitespace-pre-wrap text-sm">
                                    {billing.reconciliationNotes}
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Observações */}
                {billing.notes && (
                    <div className="bg-base-100 rounded-xl border border-base-300/50 p-6 lg:col-span-2">
                        <h2 className="text-lg font-semibold text-base-content mb-4 flex items-center gap-2">
                            <MdNotes className="text-xl" />
                            Observações
                        </h2>
                        <p className="text-base-content whitespace-pre-wrap">
                            {billing.notes}
                        </p>
                    </div>
                )}

                {/* Aprovação, transferência e conciliação */}
                <PaymentTrailSection billing={billing} />

                {/* Dados lidos do comprovante pela IA */}
                <ReceiptDataSection attachments={billing.attachments} />

                {/* Composição do atendimento (snapshot congelado na geração):
                    itens de catálogo (consultas/exames/componentes) + materiais. */}
                <BillingComposition
                    catalogItems={(billing as any).catalogItems}
                    medicalSupplies={(billing as any).medicalSupplies}
                    className="lg:col-span-2"
                />

                {/* Anexos */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 p-6 lg:col-span-2">
                    <BillingAttachmentsPanel
                        billingId={billing.id}
                        attachments={billing.attachments}
                        onChanged={loadBilling}
                        showProofWarning={
                            (billing.status === 'PENDING' ||
                                billing.status === 'OVERDUE') &&
                            !hasPaymentProof
                        }
                    />
                </div>

                {/* Contabilidade */}
                <BillingAccountingSection billingId={billing.id} />

                {/* Informações do Sistema */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 p-6 lg:col-span-2">
                    <h2 className="text-lg font-semibold text-base-content mb-4 flex items-center gap-2">
                        <MdCalendarToday className="text-xl" />
                        Informações do Sistema
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <p className="text-xs text-base-content/50">
                                Criado em
                            </p>
                            <p className="font-medium">
                                {formatDateTime(billing.createdAt)}
                            </p>
                        </div>
                        <div>
                            <p className="text-xs text-base-content/50">
                                Atualizado em
                            </p>
                            <p className="font-medium">
                                {formatDateTime(billing.updatedAt)}
                            </p>
                        </div>
                        {billing.createdBy && (
                            <div>
                                <p className="text-xs text-base-content/50">
                                    Criado por
                                </p>
                                <p className="font-medium">
                                    {billing.createdBy.name}
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <ConfirmModal
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                onConfirm={handleDelete}
                title="Excluir cobrança"
                message="Tem certeza de que deseja excluir esta cobrança? Esta ação não pode ser desfeita."
                loading={deleting}
                confirmButtonText="Excluir"
            />

            <RequestRefundModal
                isOpen={showRefundModal}
                onClose={() => setShowRefundModal(false)}
                onConfirm={handleRequestRefund}
                loading={requestingRefund}
                billing={billing}
            />

            <ConfirmModal
                isOpen={showPayModal}
                onClose={() => setShowPayModal(false)}
                onConfirm={handleMarkAsPaid}
                title="Marcar como pago"
                message={`Deseja marcar a cobrança de ${formatCurrency(billing.amount)} como paga?`}
                loading={paying}
                confirmButtonText="Confirmar pagamento"
            />

            <ConfirmModal
                isOpen={showCancelModal}
                onClose={() => setShowCancelModal(false)}
                onConfirm={handleCancel}
                title="Cancelar cobrança"
                message="Tem certeza de que deseja cancelar esta cobrança?"
                loading={cancelling}
                confirmButtonText="Cancelar cobrança"
            />

            <ConfirmModal
                isOpen={showGeneratePaymentModal}
                onClose={() => setShowGeneratePaymentModal(false)}
                onConfirm={runGeneratePayment}
                title="Regerar dados de pagamento"
                message="Esta cobrança já tem dados de pagamento gerados. Ao continuar, a cobrança atual será cancelada no gateway e novos dados (QR PIX / boleto) serão gerados. O link de pagamento continua o mesmo."
                loading={generatingPayment}
                confirmButtonText="Regerar"
            />

            {showLinkAppointmentModal && billing && (
                <LinkAppointmentModal
                    billingId={billing.id}
                    billingAmount={billing.amount}
                    currentLink={currentLink}
                    onClose={() => setShowLinkAppointmentModal(false)}
                    onLinked={loadBilling}
                />
            )}

            <ConfirmModal
                isOpen={showUnlinkModal}
                onClose={() => setShowUnlinkModal(false)}
                onConfirm={handleUnlinkAppointment}
                loading={unlinking}
                title="Desvincular do atendimento"
                message={
                    currentLink
                        ? `A cobrança deixa de pertencer a ${currentLink.typeLabel} #${currentLink.id} e volta ` +
                          'a ser avulsa. O repasse pendente gerado por ela é cancelado, e a filial não volta ' +
                          'atrás. Para atrelar a outro atendimento, use "Trocar atendimento" — não é preciso ' +
                          'desvincular antes.'
                        : ''
                }
                confirmButtonText="Desvincular"
                confirmButtonClass="btn-error"
            />

            {/* Modal Cobrar via Gateway */}
            {showChargeModal && (
                <div className="modal modal-open">
                    <div className="modal-box !max-w-lg">
                        <h3 className="text-lg font-bold mb-4">
                            Cobrar via Gateway
                        </h3>
                        <p className="text-base-content/60 mb-4">
                            Selecione o tipo de cobrança para enviar ao gateway
                            de pagamento.
                        </p>
                        <div className="space-y-4">
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Método de Pagamento
                                    </span>
                                </label>
                                <select
                                    className="select select-bordered w-full"
                                    value={chargeBillingType}
                                    onChange={e => {
                                        setChargeBillingType(
                                            e.target.value as any
                                        )
                                        if (
                                            e.target.value !== 'CREDIT_CARD' &&
                                            e.target.value !== 'DEBIT_CARD'
                                        ) {
                                            setChargeTerminalId(null)
                                        }
                                    }}
                                >
                                    <option value="PIX">PIX</option>
                                    <option value="BOLETO">Boleto</option>
                                    <option value="CREDIT_CARD">
                                        Cartão de Crédito
                                    </option>
                                    <option value="DEBIT_CARD">
                                        Cartão de Débito
                                    </option>
                                </select>
                            </div>

                            {(chargeBillingType === 'CREDIT_CARD' ||
                                chargeBillingType === 'DEBIT_CARD') &&
                                chargeTerminals.length > 0 && (
                                    <div className="form-control w-full">
                                        <label className="label">
                                            <span className="label-text font-medium">
                                                Terminal de Pagamento
                                            </span>
                                        </label>
                                        <select
                                            className="select select-bordered w-full"
                                            value={chargeTerminalId || ''}
                                            onChange={e =>
                                                setChargeTerminalId(
                                                    e.target.value
                                                        ? parseInt(
                                                              e.target.value
                                                          )
                                                        : null
                                                )
                                            }
                                        >
                                            <option value="">
                                                Sem terminal (online)
                                            </option>
                                            {chargeTerminals.map((t: any) => (
                                                <option key={t.id} value={t.id}>
                                                    {t.name} ({t.serialNumber})
                                                    -{' '}
                                                    {t.gatewayConfig?.gateway ||
                                                        'Gateway'}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}
                        </div>
                        <div className="modal-action">
                            <button
                                className="btn btn-ghost"
                                onClick={() => setShowChargeModal(false)}
                                disabled={charging}
                            >
                                Cancelar
                            </button>
                            <button
                                className="btn btn-secondary gap-2"
                                onClick={handleCharge}
                                disabled={charging}
                            >
                                {charging ? (
                                    <span className="loading loading-spinner loading-sm"></span>
                                ) : (
                                    <MdSend className="text-lg" />
                                )}
                                Enviar Cobrança
                            </button>
                        </div>
                    </div>
                    <div
                        className="modal-backdrop"
                        onClick={() => !charging && setShowChargeModal(false)}
                    ></div>
                </div>
            )}

            {/* Modal Vincular movimentação do extrato (conciliação manual) */}
            {showLinkModal && billing && (
                <div className="modal modal-open">
                    <div className="modal-box max-w-2xl">
                        <h3 className="text-lg font-bold mb-1 flex items-center gap-2">
                            <MdLink className="text-secondary" />
                            Vincular movimentação do extrato
                        </h3>
                        <p className="text-base-content/60 mb-4 text-sm">
                            Selecione o crédito recebido na conta digital que
                            corresponde a esta cobrança de{' '}
                            <strong>{formatCurrency(billing.amount)}</strong>.
                            Ao vincular, a cobrança será baixada
                            automaticamente.
                        </p>

                        <label className="input input-bordered flex items-center gap-2 mb-3">
                            <MdSearch className="text-base-content/50" />
                            <input
                                type="text"
                                className="grow"
                                placeholder="Buscar por descrição ou valor (ex.: 36,00)"
                                value={ledgerSearch}
                                onChange={e => searchLedger(e.target.value)}
                            />
                        </label>

                        <div className="max-h-72 overflow-y-auto border border-base-300/50 rounded-lg divide-y divide-base-300/40">
                            {ledgerLoading ? (
                                <div className="p-6 text-center text-base-content/50">
                                    <span className="loading loading-spinner loading-sm"></span>
                                </div>
                            ) : ledgerResults.length === 0 ? (
                                <div className="p-6 text-center text-base-content/50 text-sm">
                                    Nenhuma movimentação de crédito disponível
                                    para vincular.
                                </div>
                            ) : (
                                ledgerResults.map(entry => {
                                    const diverges =
                                        entry.amount !== billing.amount
                                    const selected =
                                        selectedLedger?.id === entry.id
                                    return (
                                        <button
                                            key={entry.id}
                                            onClick={() =>
                                                setSelectedLedger(entry)
                                            }
                                            className={`w-full text-left p-3 hover:bg-base-200/60 transition flex items-center justify-between gap-3 ${
                                                selected
                                                    ? 'bg-secondary/10'
                                                    : ''
                                            }`}
                                        >
                                            <div className="min-w-0">
                                                <p className="text-sm font-medium truncate">
                                                    {entry.description ||
                                                        'Movimentação'}
                                                </p>
                                                <p className="text-xs text-base-content/50">
                                                    {formatDateTime(
                                                        entry.occurredAt
                                                    )}
                                                </p>
                                            </div>
                                            <div className="text-right shrink-0">
                                                <p className="text-sm font-semibold text-success">
                                                    {formatCurrency(
                                                        entry.amount
                                                    )}
                                                </p>
                                                {diverges && (
                                                    <p className="text-[11px] text-warning flex items-center gap-1 justify-end">
                                                        <MdWarning /> valor
                                                        difere
                                                    </p>
                                                )}
                                            </div>
                                        </button>
                                    )
                                })
                            )}
                        </div>

                        {selectedLedger &&
                            selectedLedger.amount !== billing.amount && (
                                <div className="alert alert-warning mt-3 py-2 text-sm">
                                    <MdWarning />
                                    <span>
                                        O valor da movimentação (
                                        {formatCurrency(selectedLedger.amount)})
                                        difere do valor da cobrança (
                                        {formatCurrency(billing.amount)}).
                                        Confirme apenas se tiver certeza.
                                    </span>
                                </div>
                            )}

                        <div className="modal-action">
                            <button
                                className="btn btn-ghost"
                                onClick={() => setShowLinkModal(false)}
                                disabled={linking}
                            >
                                Cancelar
                            </button>
                            <button
                                className="btn btn-secondary gap-2"
                                onClick={handleLinkLedger}
                                disabled={linking || !selectedLedger}
                            >
                                {linking ? (
                                    <span className="loading loading-spinner loading-sm"></span>
                                ) : (
                                    <MdLink className="text-lg" />
                                )}
                                Vincular e baixar
                            </button>
                        </div>
                    </div>
                    <div
                        className="modal-backdrop"
                        onClick={() => !linking && setShowLinkModal(false)}
                    ></div>
                </div>
            )}
        </div>
    )
}
